import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import jsQR from 'jsqr';
import api from '../api/axios';
import { requiredMessage, Req, ERROR_COLOR } from '../utils/formErrors';
import {
  CalendarIcon, ClockIcon, MapPinIcon, UsersIcon,
  ArrowRightIcon, XIcon,
} from '../components/common/Icons';
import usePushNotifications from '../hooks/usePushNotifications';
import MemberCardModal from '../components/common/MemberCardModal';
import GalleryContent from '../components/common/GalleryContent';

// Portail public fidèle : passe désormais par l'instance api partagée (au
// lieu d'axios brut) pour hériter des popups 403/429 et du refresh JWT.

const STATUS_LABELS = { nouveau: 'Nouveau', membre: 'Membre régulier', serviteur: 'Serviteur', invite: 'Invité' };
const GENDER_LABELS = { homme: 'Homme', femme: 'Femme', enfant: 'Enfant' };
const MEMBER_COLORS = { nouveau: '#000000', membre: '#000000', serviteur: '#000000', invite: '#000000' };
const ACT_STATUS = {
  planned: { bg: '#dbeafe', text: '#1e40af', dot: '#3b82f6', label: 'Planifiée' },
  ongoing:  { bg: '#d1fae5', text: '#065f46', dot: '#10b981', label: 'En cours'  },
};

/* ═══════════════════════════════════════════════════════════════════════════
   ÉCRAN D'ACCUEIL — 3 choix
═══════════════════════════════════════════════════════════════════════════ */
function WelcomeScreen({ onMembre, onNouveauFidele, onVisiteur }) {
  return (
    <div style={s.wrap}>
      <div style={s.welcomeCard}>
        <div style={s.logoBlock}>
          <svg width="54" height="54" viewBox="0 0 24 24" fill="none" stroke="#1e40af" strokeWidth="1.4">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          <h1 style={s.bigTitle}>Bienvenue</h1>
          <p style={{ ...s.sub, marginTop: 12 }}>Comment souhaitez-vous accéder à l'espace ?</p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

          {/* Membre existant */}
          <button style={s.choiceBtn} onClick={onMembre}>
            <div style={{ ...s.choiceIcon, backgroundColor: '#eff6ff' }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#1e40af" strokeWidth="1.8">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            </div>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>J'ai déjà un compte</div>
              <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>Se connecter avec mon code membre</div>
            </div>
            <ArrowRightIcon size={16} color="#94a3b8"/>
          </button>

          {/* Nouveau fidèle */}
          <button style={{ ...s.choiceBtn, borderColor: '#c7d2fe' }} onClick={onNouveauFidele}>
            <div style={{ ...s.choiceIcon, backgroundColor: '#eef2ff' }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="1.8">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
                <line x1="19" y1="8" x2="19" y2="14"/>
                <line x1="22" y1="11" x2="16" y2="11"/>
              </svg>
            </div>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>Créer mon compte fidèle</div>
              <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>Je suis membre de l'église, première connexion</div>
            </div>
            <ArrowRightIcon size={16} color="#94a3b8"/>
          </button>

          {/* Visiteur externe */}
          <button style={{ ...s.choiceBtn, borderColor: '#bbf7d0' }} onClick={onVisiteur}>
            <div style={{ ...s.choiceIcon, backgroundColor: '#f0fdf4' }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.8">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>Je suis visiteur</div>
              <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>M'inscrire à une activité et recevoir ma carte</div>
            </div>
            <ArrowRightIcon size={16} color="#94a3b8"/>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CONNEXION MEMBRE (code EGL-XXXX + mot de passe)
═══════════════════════════════════════════════════════════════════════════ */
function LoginScreen({ onLogin, onBack }) {
  const [step, setStep]         = useState('code');   // 'code' | 'password'
  const [code, setCode]         = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd]   = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const doLogin = async (payload) => {
    const res = await api.post(`/participants/fidele/login/`, payload);
    return res.data;
  };

  const handleCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) { setError(requiredMessage('Code membre')); return; }
    setLoading(true); setError('');
    try {
      const data = await doLogin({ code: code.trim() });
      onLogin(data);
    } catch (err) {
      const d = err.response?.data;
      if (d?.needs_password) { setStep('password'); }
      else if (err.response?.status === 429) { setError('Trop de tentatives. Veuillez réessayer dans une minute.'); }
      else { setError(d?.error || "Code invalide. Contactez l'accueil."); }
    } finally { setLoading(false); }
  };

  const handlePassword = async (e) => {
    e.preventDefault();
    if (!password.trim()) { setError(requiredMessage('Mot de passe')); return; }
    setLoading(true); setError('');
    try {
      const data = await doLogin({ code: code.trim(), password: password.trim() });
      onLogin(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Mot de passe incorrect.');
    } finally { setLoading(false); }
  };

  return (
    <div style={s.wrap}>
      <div style={s.card}>
        <BtnBack onClick={step === 'password' ? () => { setStep('code'); setPassword(''); setError(''); } : onBack} />

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{ width: '60px', height: '60px', borderRadius: '18px', background: 'linear-gradient(135deg,#1e40af,#3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1e293b', margin: '0 0 4px' }}>
            {step === 'code' ? 'Espace Membre' : 'Mot de passe'}
          </h1>
          <p style={{ fontSize: '0.85rem', color: '#64748b', margin: 0 }}>
            {step === 'code' ? 'Entrez votre code membre pour accéder' : `Code : ${code} — entrez votre mot de passe`}
          </p>
        </div>

        {step === 'code' ? (
          <form onSubmit={handleCode} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {error && <Err msg={error} />}
            <div>
              <label style={s.label}>Code membre<Req /></label>
              <input
                style={{ ...s.input, textTransform: 'uppercase', letterSpacing: '0.14em', fontSize: '1.25rem', textAlign: 'center', fontWeight: 800, padding: '13px' }}
                value={code} onChange={e => setCode(e.target.value.toUpperCase())}
                placeholder="0000 ou EGL-0000" autoComplete="off" autoFocus maxLength={20}
              />
              <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '6px', textAlign: 'center' }}>
                Ce code figure sur votre carte de membre (les 4 chiffres suffisent)
              </p>
            </div>
            <button type="submit" style={{ ...s.btnPrimary, padding: '13px', fontSize: '0.95rem' }} disabled={loading}>
              {loading ? 'Vérification...' : 'Continuer'}
            </button>
          </form>
        ) : (
          <form onSubmit={handlePassword} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {error && <Err msg={error} />}
            <div>
              <label style={s.label}>Mot de passe<Req /></label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPwd ? 'text' : 'password'}
                  style={{ ...s.input, paddingRight: '44px', fontSize: '1.1rem', padding: '13px 44px 13px 13px' }}
                  value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Votre mot de passe" autoFocus
                />
                <button type="button" onClick={() => setShowPwd(v => !v)}
                  style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '4px' }}>
                  {showPwd
                    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  }
                </button>
              </div>
            </div>
            <button type="submit" style={{ ...s.btnPrimary, padding: '13px', fontSize: '0.95rem' }} disabled={loading}>
              {loading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CRÉATION COMPTE FIDÈLE (auto-inscription)
═══════════════════════════════════════════════════════════════════════════ */
function NouveauFideleForm({ onCreated, onBack, onLogin }) {
  const [church, setChurch] = useState(null);
  const [form, setForm]   = useState({ first_name: '', last_name: '', gender: '', phone: '', status: 'membre', password: '', hasPassword: false });
  const [errors, setErr]  = useState({});
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [requirePassword, setRequirePassword] = useState(false);

  const churchId = church?.id || null;

  useEffect(() => {
    if (!churchId) return;
    api.get(`/notifications/church-settings/?church_id=${churchId}`)
      .then(r => {
        const req = r.data.require_password ?? false;
        setRequirePassword(req);
        if (req) setForm(f => ({ ...f, hasPassword: true }));
      })
      .catch(() => {});
  }, [churchId]);

  if (!church) {
    return <ChurchSelectScreen onSelect={setChurch} onBack={onBack} />;
  }

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = requiredMessage('Prénom');
    if (!form.last_name.trim())  e.last_name  = requiredMessage('Nom');
    if (!form.gender)            e.gender     = requiredMessage('Genre');
    if (form.hasPassword && !form.password.trim()) e.password = requiredMessage('Mot de passe');
    else if (form.hasPassword && form.password.length < 4) e.password = 'Le mot de passe doit contenir au moins 4 caractères.';
    setErr(e); return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (!form.hasPassword) delete payload.password;
      if (churchId) payload.church_id = churchId;
      const res = await api.post(`/participants/fidele/auto-inscription/`, payload);
      onCreated(res.data);
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === 'object') setErr(data);
      else setErr({ global: 'Une erreur est survenue.' });
    } finally { setSaving(false); }
  };

  return (
    <div style={s.wrap}>
      <div style={{ ...s.card, maxWidth: '500px' }}>
        <BtnBack onClick={onBack} />
        <div style={s.logoBlock}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
            <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="1.5">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
              <line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>
            </svg>
          </div>
          <h1 style={{ ...s.bigTitle, color: '#4f46e5' }}>Créer mon compte</h1>
          <p style={s.sub}>Remplissez vos informations pour rejoindre l'espace fidèle</p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '13px' }}>
          {errors.global && <Err msg={errors.global} />}
          {errors.account_exists && <AccountExists msg={errors.phone} onLogin={onLogin} />}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '11px' }}>
            <Field label="Prénom *" error={errors.first_name}>
              <input style={{ ...s.input, borderColor: errors.first_name ? '#ef4444' : '#d1d5db' }}
                value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} placeholder="Votre prénom" />
            </Field>
            <Field label="Nom *" error={errors.last_name}>
              <input style={{ ...s.input, borderColor: errors.last_name ? '#ef4444' : '#d1d5db' }}
                value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} placeholder="Votre nom" />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '11px' }}>
            <Field label="Genre *" error={errors.gender}>
              <select style={{ ...s.input, borderColor: errors.gender ? '#ef4444' : '#d1d5db', backgroundColor: 'white' }}
                value={form.gender} onChange={e => setForm({ ...form, gender: e.target.value })}>
                <option value="">Sélectionner</option>
                <option value="homme">Homme</option>
                <option value="femme">Femme</option>
                <option value="enfant">Enfant</option>
              </select>
            </Field>
            <Field label="Statut">
              <select style={{ ...s.input, backgroundColor: 'white' }}
                value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                <option value="membre">Membre régulier</option>
                <option value="nouveau">Nouveau</option>
                <option value="serviteur">Serviteur</option>
              </select>
            </Field>
          </div>

          <Field label="Téléphone">
            <input style={{ ...s.input, borderColor: errors.account_exists ? '#ef4444' : '#d1d5db' }} value={form.phone}
              onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="6XX XXX XXX" />
          </Field>

          {/* Option mot de passe — checkbox si optionnel, champ direct si obligatoire */}
          {requirePassword ? (
            <div style={{ backgroundColor: '#fffbeb', borderRadius: '10px', padding: '12px 14px', border: '1px solid #fcd34d' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '10px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#92400e' }}>Mot de passe obligatoire</span>
              </div>
              <Field label="Mot de passe *" error={errors.password}>
                <div style={{ position: 'relative' }}>
                  <input type={showPassword ? 'text' : 'password'} style={{ ...s.input, borderColor: errors.password ? '#ef4444' : '#fcd34d', paddingRight: '44px' }}
                    value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                    placeholder="Minimum 4 caractères" autoFocus />
                  <button type="button" onClick={() => setShowPassword(v => !v)}
                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '4px' }}>
                    {showPassword
                      ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    }
                  </button>
                </div>
              </Field>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', backgroundColor: '#eef2ff', borderRadius: '10px' }}>
                <input type="checkbox" id="hasPassword" checked={form.hasPassword}
                  onChange={e => setForm({ ...form, hasPassword: e.target.checked, password: '' })}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                <label htmlFor="hasPassword" style={{ fontSize: '0.9rem', color: '#374151', cursor: 'pointer', flex: 1, margin: 0 }}>
                  <span style={{ fontWeight: 700 }}>Protéger mon compte</span>
                  <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>
                    {form.hasPassword ? 'Connexion avec ID + mot de passe' : 'Connexion avec ID uniquement'}
                  </div>
                </label>
              </div>
              {form.hasPassword && (
                <Field label="Mot de passe *" error={errors.password}>
                  <div style={{ position: 'relative' }}>
                    <input type={showPassword ? 'text' : 'password'} style={{ ...s.input, borderColor: errors.password ? '#ef4444' : '#d1d5db', paddingRight: '44px' }}
                      value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                      placeholder="Minimum 4 caractères" />
                    <button type="button" onClick={() => setShowPassword(v => !v)}
                      style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '4px' }}>
                      {showPassword
                        ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      }
                    </button>
                  </div>
                </Field>
              )}
            </>
          )}

          <button type="submit" style={{ ...s.btnPrimary, backgroundColor: '#4f46e5' }} disabled={saving}>
            {saving ? 'Création...' : 'Créer mon compte et accéder à mon espace'}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   FORMULAIRE VISITEUR EXTERNE
═══════════════════════════════════════════════════════════════════════════ */
function VisiteurForm({ onBack, onCreated, onLogin }) {
  const [church, setChurch]     = useState(null);
  const [activities, setActs]   = useState([]);
  const [loadingActs, setLoadA] = useState(true);
  const [form, setForm]         = useState({ first_name: '', last_name: '', gender: '', phone: '', activity_id: '' });
  const [errors, setErr]        = useState({});
  const [saving, setSaving]     = useState(false);

  const churchId = church?.id || null;

  useEffect(() => {
    if (!churchId) return;
    api.get(`/participants/fidele/activities/?church_id=${churchId}`)
      .then(r => setActs(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setLoadA(false));
  }, [churchId]);

  if (!church) {
    return <ChurchSelectScreen onSelect={setChurch} onBack={onBack} />;
  }

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = requiredMessage('Prénom');
    if (!form.last_name.trim())  e.last_name  = requiredMessage('Nom');
    if (!form.gender)            e.gender     = requiredMessage('Genre');
    setErr(e); return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (churchId) payload.church_id = churchId;
      const res = await api.post(`/participants/visiteur/inscription/`, payload);
      const data = res.data;
      onCreated({
        id:          data.id,
        first_name:  data.first_name,
        last_name:   data.last_name,
        gender:      data.gender,
        phone:       data.phone || '',
        member_code: data.member_code,
        qr_code:     data.qr_code,
        status:      'invite',
        photo:       null,
        _visitor_activity: data.activity || null,
        church,
      });
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === 'object') setErr(data);
      else setErr({ global: 'Une erreur est survenue.' });
    } finally { setSaving(false); }
  };

  return (
    <div style={s.wrap}>
      <div style={{ ...s.card, maxWidth: '520px' }}>
        <BtnBack onClick={onBack} />
        <div style={s.logoBlock}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
            <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.5">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <h1 style={{ ...s.bigTitle, color: '#15803d' }}>Inscription Visiteur</h1>
          <p style={s.sub}>Créez votre compte et rejoignez l'église</p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '13px' }}>
          {errors.global && <Err msg={errors.global} />}
          {errors.account_exists && <AccountExists msg={errors.phone} onLogin={onLogin} />}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '11px' }}>
            <Field label="Prénom *" error={errors.first_name}>
              <input style={{ ...s.input, borderColor: errors.first_name ? '#ef4444' : '#d1d5db' }}
                value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} placeholder="Votre prénom" />
            </Field>
            <Field label="Nom *" error={errors.last_name}>
              <input style={{ ...s.input, borderColor: errors.last_name ? '#ef4444' : '#d1d5db' }}
                value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} placeholder="Votre nom" />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '11px' }}>
            <Field label="Genre *" error={errors.gender}>
              <select style={{ ...s.input, borderColor: errors.gender ? '#ef4444' : '#d1d5db', backgroundColor: 'white' }}
                value={form.gender} onChange={e => setForm({ ...form, gender: e.target.value })}>
                <option value="">Sélectionner</option>
                <option value="homme">Homme</option>
                <option value="femme">Femme</option>
                <option value="enfant">Enfant</option>
              </select>
            </Field>
            <Field label="Téléphone">
              <input style={{ ...s.input, borderColor: errors.account_exists ? '#ef4444' : '#d1d5db' }} value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="6XX XXX XXX" />
            </Field>
          </div>
          <Field label="Activité (optionnel)">
            {loadingActs
              ? <div style={{ color: '#64748b', fontSize: '0.85rem' }}>Chargement...</div>
              : activities.length === 0
                ? <div style={{ fontSize: '0.82rem', color: '#94a3b8', padding: '8px 12px', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #e2e8f0' }}>
                    Aucune activité disponible — vous pouvez quand même créer votre compte
                  </div>
                : <select style={{ ...s.input, backgroundColor: 'white' }}
                    value={form.activity_id} onChange={e => setForm({ ...form, activity_id: e.target.value })}>
                    <option value="">Aucune activité (créer le compte uniquement)</option>
                    {activities.map(a => (
                      <option key={a.id} value={a.id}>{a.name} — {a.date} à {a.start_time?.slice(0,5)}</option>
                    ))}
                  </select>
            }
          </Field>
          <button type="submit" style={{ ...s.btnPrimary, backgroundColor: '#16a34a' }} disabled={saving}>
            {saving ? 'Inscription...' : form.activity_id ? "S'inscrire et obtenir ma carte d'invitation" : "Créer mon compte visiteur"}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CARTE D'INVITATION VISITEUR
═══════════════════════════════════════════════════════════════════════════ */
// eslint-disable-next-line no-unused-vars
function CarteInvitation({ result, onBack, churchId }) {
  const cardRef = useRef(null);
  const act     = result.activity;
  const color   = act?.activity_type_color || '#16a34a';
  const [churchSettings, setChurchSettings] = useState({ church_name: 'RCC', logo: null });

  useEffect(() => {
    const url = churchId
      ? `/notifications/church-settings/?church_id=${churchId}`
      : `/notifications/church-settings/`;
    api.get(url)
      .then(r => setChurchSettings(r.data))
      .catch(() => {});
  }, [churchId]);

  const initials = `${result.first_name?.[0] || ''}${result.last_name?.[0] || ''}`.toUpperCase();

  const download = async () => {
    if (!cardRef.current) return;
    const canvas = await html2canvas(cardRef.current, { scale: 4, backgroundColor: '#ffffff', useCORS: true });
    const a = document.createElement('a');
    a.download = `invitation-${result.first_name}-${result.last_name}.png`;
    a.href = canvas.toDataURL('image/png'); a.click();
  };

  return (
    <div style={s.wrap}>
      <div style={{ maxWidth: '480px', width: '100%', padding: '0 12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <BtnBack onClick={onBack} label="Nouvel accueil" />
          <button style={s.btnWhite} onClick={download}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Télécharger
          </button>
        </div>

        {/* Bandeau confirmation */}
        <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '11px 14px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
          <div>
            <div style={{ fontWeight: 700, color: '#15803d', fontSize: '0.9rem' }}>Inscription confirmée !</div>
            <div style={{ fontSize: '0.78rem', color: '#16a34a' }}>{result.first_name} {result.last_name} — « {act.name} »</div>
          </div>
        </div>

        {/* ── CARTE ── */}
        <div ref={cardRef} style={{
          borderRadius: '16px', overflow: 'hidden',
          boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          background: '#ffffff',
          display: 'flex', flexDirection: 'column',
          position: 'relative',
        }}>
          {/* Fond décoratif */}
          <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            <div style={{ position: 'absolute', top: '-40px', right: '-50px', width: '180px', height: '180px', borderRadius: '50%', background: `${color}12` }}/>
            <div style={{ position: 'absolute', bottom: '-20px', left: '-30px', width: '120px', height: '120px', borderRadius: '50%', background: `${color}08` }}/>
            {churchSettings.logo && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.07, pointerEvents: 'none' }}>
                <img src={churchSettings.logo} alt="" style={{ width: '60%', objectFit: 'contain' }} crossOrigin="anonymous"/>
              </div>
            )}
          </div>

          {/* EN-TÊTE */}
          <div style={{ background: `linear-gradient(120deg, ${color} 0%, ${color}d0 100%)`, padding: '9px 14px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ backgroundColor: 'rgba(255,255,255,0.22)', borderRadius: '7px', padding: '5px', display: 'flex' }}>
                {churchSettings.logo
                  ? <img src={churchSettings.logo} alt="logo" style={{ width: '20px', height: '20px', objectFit: 'contain' }} crossOrigin="anonymous" />
                  : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                }
              </div>
              <div>
                <div style={{ fontSize: '0.9rem', fontWeight: 900, color: 'white', lineHeight: 1.1 }}>{churchSettings.church_name}</div>
                <div style={{ fontSize: '0.48rem', color: 'rgba(255,255,255,0.7)', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase' }}>Carte d'invitation</div>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.48rem', color: 'rgba(255,255,255,0.6)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Code d'accès</div>
              <div style={{ fontFamily: 'monospace', fontWeight: 900, fontSize: '0.72rem', color: 'white', letterSpacing: '0.06em' }}>{result.member_code}</div>
            </div>
          </div>

          {/* CORPS */}
          <div style={{ flex: 1, display: 'flex', gap: '14px', padding: '12px 14px 10px' }}>
            {/* Avatar */}
            <div style={{ flexShrink: 0 }}>
              <div style={{ width: '96px', height: '118px', borderRadius: '8px', overflow: 'hidden', boxShadow: `0 4px 18px ${color}40`, backgroundColor: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: `${color}25`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: '1.4rem', fontWeight: 900, color }}>{initials}</span>
                  </div>
                  <span style={{ fontSize: '0.48rem', color: `${color}80`, textAlign: 'center', lineHeight: 1.3 }}>Visiteur</span>
                </div>
              </div>
            </div>

            {/* Infos droite */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              {/* Nom + statut */}
              <div>
                <div style={{ fontSize: '1.05rem', fontWeight: 900, color: '#0f172a', lineHeight: 1.1, letterSpacing: '-0.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {result.first_name} <span style={{ color }}>{result.last_name}</span>
                </div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '4px', background: `${color}14`, borderRadius: '20px', padding: '2px 8px' }}>
                  <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: color }}/>
                  <span style={{ fontSize: '0.62rem', fontWeight: 700, color }}>Visiteur · {GENDER_LABELS[result.gender]}</span>
                </div>
              </div>

              {/* Activité */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                <div style={{ fontSize: '0.45rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Activité</div>
                <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{act.name}</div>
                <div style={{ fontSize: '0.64rem', color: '#64748b' }}>{act.date}{act.start_time ? ` · ${act.start_time.slice(0,5)}` : ''}</div>
                <div style={{ fontSize: '0.62rem', color: '#94a3b8' }}>{act.location}</div>
              </div>

              {/* QR + code */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <div>
                  <div style={{ fontSize: '0.46rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Code</div>
                  <div style={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 900, color, letterSpacing: '0.1em' }}>{result.member_code}</div>
                </div>
                <div style={{ borderRadius: '6px', padding: '3px', background: 'white', boxShadow: `0 2px 10px ${color}20`, border: `1px solid ${color}18` }}>
                  <QRCodeSVG value={result.member_code} size={46} fgColor={color} level="M"/>
                </div>
              </div>
            </div>
          </div>

          {/* PIED */}
          <div style={{ background: `linear-gradient(120deg, ${color}ee 0%, ${color}aa 100%)`, padding: '5px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <span style={{ fontSize: '0.48rem', color: 'rgba(255,255,255,0.78)', letterSpacing: '0.05em' }}>Conservez ce code pour vous reconnecter</span>
            <span style={{ fontSize: '0.46rem', color: 'rgba(255,255,255,0.5)', fontFamily: 'monospace' }}>{result.member_code}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SCANNER QR — caméra pour marquer présence
═══════════════════════════════════════════════════════════════════════════ */
function QRScannerModal({ participant, onClose, onSuccess }) {
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const rafRef    = useRef(null);
  const streamRef = useRef(null);

  const [status, setStatus]   = useState('scanning');
  const [result, setResult]   = useState(null);
  const [errMsg, setErrMsg]   = useState('');
  const [camErr, setCamErr]   = useState('');
  const [sending, setSending] = useState(false);
  const [facingMode, setFacingMode] = useState('environment'); // 'environment' = arrière, 'user' = avant

  const stopCamera = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
  }, []);

  const processFrame = useCallback(() => {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
      rafRef.current = requestAnimationFrame(processFrame);
      return;
    }
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(video, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: 'dontInvert' });
    if (code) {
      stopCamera();
      handleQRFound(code.data);
    } else {
      rafRef.current = requestAnimationFrame(processFrame);
    }
  }, [stopCamera]); // eslint-disable-line react-hooks/exhaustive-deps

  const startCamera = useCallback((facing) => {
    stopCamera();
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: facing } })
      .then(stream => {
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        video.onloadedmetadata = () => {
          video.play()
            .then(() => { rafRef.current = requestAnimationFrame(processFrame); })
            .catch(() => {});
        };
      })
      .catch(() => setCamErr("Impossible d'accéder à la caméra. Autorisez l'accès dans les paramètres."));
  }, [stopCamera, processFrame]); // eslint-disable-line

  useEffect(() => {
    startCamera(facingMode);
    return () => stopCamera();
  }, []); // eslint-disable-line

  const switchCamera = () => {
    const newFacing = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(newFacing);
    startCamera(newFacing);
  };

  const handleQRFound = async (qrData) => {
    // Le QR encode l'URL : http://...../scan?activity=<uuid>
    // ou directement l'UUID de l'activité
    let activityQr = qrData;
    try {
      const url = new URL(qrData);
      activityQr = url.searchParams.get('activity') || qrData;
    } catch { /* qrData est directement un UUID */ }

    setSending(true);
    try {
      const res = await api.post(`/attendance/scan-qr/`, {
        activity_qr: activityQr,
        participant_qr: participant.qr_code,
      });
      setResult(res.data);
      const newStatus = res.data.already_recorded ? 'already' : 'success';
      setStatus(newStatus);
      if (newStatus === 'success' && onSuccess) onSuccess();
    } catch (err) {
      setErrMsg(err.response?.data?.error || 'QR invalide ou activité introuvable.');
      setStatus('error');
    } finally {
      setSending(false);
    }
  };

  const retry = () => {
    setStatus('scanning');
    setResult(null);
    setErrMsg('');
    startCamera(facingMode);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>

      {/* Header */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 10 }}>
        <div style={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>Scanner le QR de l'activité</div>
        <button onClick={() => { stopCamera(); onClose(); }} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      {/* Viewfinder ou résultat */}
      {status === 'scanning' && !camErr && (
        <>
          <div style={{ position: 'relative', width: '280px', height: '280px' }}>
            <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '16px', transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }} playsInline muted />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            {/* Viseur */}
            <div style={{ position: 'absolute', inset: 0, borderRadius: '16px', border: '2px solid rgba(255,255,255,0.3)' }}>
              {[['0','0','right','bottom'],['0','auto','right','bottom'],['auto','0','left','bottom'],['auto','auto','left','top']].map(([t,b,br,or],i) => (
                <div key={i} style={{ position: 'absolute', top: t==='0'?'12px':undefined, bottom: b==='0'?'12px':undefined, left: br==='left'?'12px':undefined, right: br==='right'?'12px':undefined, width: '24px', height: '24px', borderTop: or==='top'?'none':`3px solid white`, borderBottom: or==='bottom'?'none':`3px solid white`, borderLeft: br==='left'?`3px solid white`:'none', borderRight: br==='right'?`3px solid white`:'none' }}/>
              ))}
            </div>
            {/* Bouton switch caméra */}
            <button onClick={switchCamera}
              title={facingMode === 'environment' ? 'Passer à la caméra avant' : 'Passer à la caméra arrière'}
              style={{ position: 'absolute', bottom: 10, right: 10, width: 40, height: 40, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', border: '1.5px solid rgba(255,255,255,0.4)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', transition: 'background .15s' }}
              onMouseEnter={e => e.currentTarget.style.background='rgba(0,0,0,0.75)'}
              onMouseLeave={e => e.currentTarget.style.background='rgba(0,0,0,0.55)'}>
              {/* Icône switch caméra */}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 7h-3a2 2 0 0 1-2-2V2"/>
                <path d="M9 2H4a2 2 0 0 0-2 2v4"/>
                <path d="M4 17H2v4h4v-2"/>
                <path d="M20 17v4h-4v-2"/>
                <rect x="8" y="6" width="8" height="12" rx="2"/>
                <circle cx="12" cy="12" r="2"/>
              </svg>
            </button>
          </div>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.82rem', marginTop: '12px', textAlign: 'center' }}>
            {sending ? 'Enregistrement...' : facingMode === 'environment' ? 'Caméra arrière · Pointez vers le QR code' : 'Caméra avant · Pointez vers le QR code'}
          </p>
        </>
      )}

      {camErr && (
        <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', maxWidth: '320px', textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>📷</div>
          <div style={{ fontWeight: 700, color: '#1e293b', marginBottom: '8px' }}>Caméra non disponible</div>
          <div style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: '16px' }}>{camErr}</div>
          <button onClick={() => { stopCamera(); onClose(); }} style={{ width: '100%', padding: '11px', backgroundColor: '#1e40af', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 700 }}>Fermer</button>
        </div>
      )}

      {(status === 'success' || status === 'already' || status === 'error') && (
        <div style={{ backgroundColor: 'white', borderRadius: '20px', padding: '28px 24px', maxWidth: '340px', width: '90%', textAlign: 'center' }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', backgroundColor: status === 'success' ? '#dcfce7' : status === 'already' ? '#fef9c3' : '#fee2e2' }}>
            {status === 'success' && <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>}
            {status === 'already' && <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>}
            {status === 'error' && <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>}
          </div>
          <div style={{ fontWeight: 800, fontSize: '1.15rem', color: '#1e293b', marginBottom: '6px' }}>
            {status === 'success' ? 'Présence confirmée !' : status === 'already' ? 'Déjà enregistré' : 'Erreur'}
          </div>
          {(status === 'success' || status === 'already') && result?.participant_name && (
            <div style={{ marginBottom: '10px' }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>{result.participant_name}</div>
              {result.activity_name && <div style={{ fontSize: '0.82rem', color: '#6366f1', fontWeight: 600, marginTop: '2px' }}>{result.activity_name}</div>}
            </div>
          )}
          <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: status === 'success' ? '12px' : '20px' }}>
            {result?.message || errMsg}
          </div>
          {status === 'success' && result?.recorded_at_display && (
            <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '8px 14px', fontSize: '0.82rem', color: '#166534', fontWeight: 600, marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              {result.recorded_at_display}
            </div>
          )}
          {status === 'already' && result?.recorded_at_display && (
            <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '8px 14px', fontSize: '0.82rem', color: '#92400e', fontWeight: 600, marginBottom: '20px' }}>
              Enregistré le {result.recorded_at_display}
            </div>
          )}
          <div style={{ display: 'flex', gap: '10px' }}>
            {status !== 'success' && (
              <button onClick={retry} style={{ flex: 1, padding: '11px', backgroundColor: '#eff6ff', color: '#1e40af', border: '1.5px solid #bfdbfe', borderRadius: '10px', cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem' }}>
                Réessayer
              </button>
            )}
            <button onClick={() => { stopCamera(); onClose(); }} style={{ flex: 1, padding: '11px', backgroundColor: '#1e40af', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem' }}>
              {status === 'success' ? 'Fermer' : 'Annuler'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CARTE MEMBRE ID (téléchargeable) — style carte officielle
═══════════════════════════════════════════════════════════════════════════ */
const CHURCH_NAME = 'RCC'; // fallback, overridden by ChurchSettings API


/* ═══════════════════════════════════════════════════════════════════════════
   MODAL INSCRIPTION À UNE ACTIVITÉ
═══════════════════════════════════════════════════════════════════════════ */
function RegisterModal({ activity, participant, onClose, onSuccess }) {
  const [step, setStep]       = useState('confirm'); // confirm | loading | success | error
  const [error, setError]     = useState('');

  const handle = async () => {
    setStep('loading');
    try {
      const res = await api.post(`/participants/fidele/register/`, {
        qr_code: participant.qr_code, activity_id: activity.id,
      });
      setStep('success');
      // Notify parent after 2s so user sees the success screen
      setTimeout(() => onSuccess(res.data.message), 2200);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'inscription.');
      setStep('error');
    }
  };

  const sc = ACT_STATUS[activity.status] || ACT_STATUS.planned;

  // ── Écran succès
  if (step === 'success') {
    return (
      <div style={s.overlay}>
        <div style={{ ...s.modal, textAlign: 'center', padding: '36px 28px' }}>
          <div style={{ width: '72px', height: '72px', borderRadius: '50%', backgroundColor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px', animation: 'popIn 0.4s cubic-bezier(0.34,1.56,0.64,1)' }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <div style={{ fontWeight: 800, fontSize: '1.2rem', color: '#1e293b', marginBottom: '8px' }}>Inscription confirmée !</div>
          <div style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '16px' }}>
            Vous êtes inscrit(e) à <strong style={{ color: '#1e293b' }}>{activity.name}</strong>
          </div>
          <div style={{ backgroundColor: '#f0fdf4', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', textAlign: 'left' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <CalendarIcon size={13} color="#16a34a"/>
              <span style={{ fontSize: '0.85rem', color: '#166534', fontWeight: 600 }}>{activity.date}{activity.start_time ? ` à ${activity.start_time.slice(0,5)}` : ''}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <MapPinIcon size={13} color="#16a34a"/>
              <span style={{ fontSize: '0.85rem', color: '#166534' }}>{activity.location}</span>
            </div>
          </div>
          <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: '20px' }}>
            Pensez à scanner le QR code de l'activité le jour J pour confirmer votre présence.
          </div>
          <button style={{ ...s.btnPrimary, width: '100%' }} onClick={onClose}>Fermer</button>
        </div>
        <style>{`@keyframes popIn { from { transform: scale(0.5); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
      </div>
    );
  }

  return (
    <div style={s.overlay} onClick={step === 'confirm' ? onClose : undefined}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        {step === 'loading' ? (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{ width: '48px', height: '48px', border: '4px solid #e2e8f0', borderTopColor: '#1e40af', borderRadius: '50%', margin: '0 auto 16px', animation: 'spin 0.8s linear infinite' }} />
            <div style={{ color: '#64748b', fontWeight: 600 }}>Inscription en cours...</div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h2 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>Inscription à l'activité</h2>
              <button style={s.btnClose} onClick={onClose}><XIcon size={15}/></button>
            </div>
            <div style={{ backgroundColor: '#f8fafc', borderRadius: '8px', padding: '13px', borderLeft: `4px solid ${activity.activity_type_color || '#3b82f6'}`, marginBottom: '13px' }}>
              <div style={{ fontWeight: 700, color: '#1e293b', marginBottom: '7px' }}>{activity.name}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                <MetaRow icon={<CalendarIcon size={13} color="#64748b"/>}>{activity.date}</MetaRow>
                <MetaRow icon={<ClockIcon size={13} color="#64748b"/>}>{activity.start_time?.slice(0,5)}{activity.end_time ? ` — ${activity.end_time.slice(0,5)}` : ''}</MetaRow>
                <MetaRow icon={<MapPinIcon size={13} color="#64748b"/>}>{activity.location}</MetaRow>
              </div>
              <span style={{ ...s.badge, backgroundColor: sc.bg, color: sc.text, marginTop: '7px', display: 'inline-block' }}>{sc.label}</span>
            </div>
            <div style={{ backgroundColor: '#f0fdf4', borderRadius: '7px', padding: '9px 13px', fontSize: '0.86rem', color: '#166534', marginBottom: '13px' }}>
              Inscription en tant que <strong>{participant.first_name} {participant.last_name}</strong>
            </div>
            {step === 'error' && <Err msg={error} />}
            <div style={{ display: 'flex', gap: '9px', marginTop: '4px' }}>
              <button style={s.btnSecondary} onClick={onClose}>Annuler</button>
              <button style={{ ...s.btnPrimary, flex: 1 }} onClick={handle}>
                {step === 'error' ? 'Réessayer' : 'Confirmer mon inscription'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MINI-CALENDRIER FIDÈLE (sans router)
═══════════════════════════════════════════════════════════════════════════ */
const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const DAYS_FR   = ['L','M','M','J','V','S','D'];

function MiniCalendar({ activities, registeredIds, color }) {
  const today = new Date();
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [popup, setPopup] = useState(null);
  const [showPicker, setShowPicker] = useState(false);

  const daysInMonth  = new Date(year, month + 1, 0).getDate();
  const firstDay     = (() => { const d = new Date(year, month, 1).getDay(); return d === 0 ? 6 : d - 1; })();

  // Étend les activités récurrentes pour le mois affiché
  const byDay = {};
  const pad = n => String(n).padStart(2, '0');

  activities.forEach(a => {
    if (!a.date) return;

    if (!a.is_recurring || !a.recurrence_days?.length) {
      // Activité ponctuelle
      const [y, m, d] = a.date.split('-').map(Number);
      if (y === year && m - 1 === month) {
        if (!byDay[d]) byDay[d] = [];
        byDay[d].push({ ...a });
      }
    } else {
      // Activité récurrente : on génère toutes les occurrences du mois
      const startDate = new Date(a.date);
      const endDate   = a.recurrence_end ? new Date(a.recurrence_end) : new Date(year + 5, 11, 31);
      const mStart    = new Date(year, month, 1);
      const mEnd      = new Date(year, month, daysInMonth);
      const from      = startDate > mStart ? startDate : mStart;
      const to        = endDate   < mEnd   ? endDate   : mEnd;

      for (let dt = new Date(from); dt <= to; dt.setDate(dt.getDate() + 1)) {
        const jsDay = dt.getDay();
        const idx   = jsDay === 0 ? 6 : jsDay - 1; // 0=Lun…6=Dim
        if (a.recurrence_days.includes(idx)) {
          const d = dt.getDate();
          if (!byDay[d]) byDay[d] = [];
          byDay[d].push({
            ...a,
            date: `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(d)}`,
          });
        }
      }
    }
  });

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isToday = (d) => d === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  return (
    <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', position: 'relative' }}>
      {/* Navigation mois — avec sélecteur rapide */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', position: 'relative' }}>
        <button onClick={() => { if (month === 0) { setMonth(11); setYear(y => y-1); } else setMonth(m => m-1); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px 10px', borderRadius: '8px', color: '#64748b', fontSize: '1.1rem', fontWeight: 700, lineHeight: 1 }}>‹</button>

        {/* Titre cliquable → ouvre le sélecteur */}
        <button onClick={() => setShowPicker(v => !v)}
          style={{ display: 'flex', alignItems: 'center', gap: 5, fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', background: showPicker ? '#f1f5f9' : 'transparent', border: 'none', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', transition: 'background .15s' }}>
          {MONTHS_FR[month]} {year}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transform: showPicker ? 'rotate(180deg)' : 'none', transition: 'transform .2s', color: '#94a3b8' }}><polyline points="6 9 12 15 18 9"/></svg>
        </button>

        <button onClick={() => { if (month === 11) { setMonth(0); setYear(y => y+1); } else setMonth(m => m+1); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px 10px', borderRadius: '8px', color: '#64748b', fontSize: '1.1rem', fontWeight: 700, lineHeight: 1 }}>›</button>

        {/* Panneau sélecteur mois/année */}
        {showPicker && (
          <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', zIndex: 20, background: 'white', borderRadius: 14, boxShadow: '0 8px 32px rgba(0,0,0,0.15)', border: '1px solid #e2e8f0', padding: 16, marginTop: 6, minWidth: 260 }}>
            {/* Sélecteur année */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <button onClick={() => setYear(y => y - 1)} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <span style={{ fontWeight: 800, fontSize: '1rem', color: '#1e293b' }}>{year}</span>
              <button onClick={() => setYear(y => y + 1)} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </div>
            {/* Grille 12 mois */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
              {MONTHS_FR.map((m, i) => (
                <button key={i} onClick={() => { setMonth(i); setShowPicker(false); }}
                  style={{ padding: '7px 4px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, background: month === i && year === year ? color : '#f8fafc', color: month === i ? 'white' : '#374151', transition: 'all .15s' }}
                  onMouseEnter={e => { if (month !== i) e.currentTarget.style.background = '#e2e8f0'; }}
                  onMouseLeave={e => { if (month !== i) e.currentTarget.style.background = '#f8fafc'; }}>
                  {m.slice(0, 3)}
                </button>
              ))}
            </div>
            {/* Bouton Aujourd'hui */}
            <button onClick={() => { setMonth(today.getMonth()); setYear(today.getFullYear()); setShowPicker(false); }}
              style={{ width: '100%', marginTop: 10, padding: '8px', borderRadius: 8, border: `1px solid ${color}30`, background: `${color}10`, color: color, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>
              Aujourd'hui
            </button>
          </div>
        )}
      </div>

      {/* Jours semaine */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '4px' }}>
        {DAYS_FR.map((d, i) => (
          <div key={i} style={{ textAlign: 'center', fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', padding: '3px 0' }}>{d}</div>
        ))}
      </div>

      {/* Grille jours */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
        {cells.map((d, i) => {
          const hasEvents = d && byDay[d];
          const isReg = hasEvents && byDay[d].some(a => registeredIds.has(a.id));
          return (
            <div key={i} onClick={() => hasEvents && setPopup({ day: d, events: byDay[d] })}
              style={{
                aspectRatio: '1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                borderRadius: '8px', cursor: hasEvents ? 'pointer' : 'default',
                backgroundColor: hasEvents && !isToday(d) ? color + '12' : 'transparent',
                border: isToday(d) ? `2px solid ${color}` : '2px solid transparent',
                position: 'relative',
              }}>
              {d && (
                <>
                  <span style={{ fontSize: '0.78rem', fontWeight: isToday(d) ? 800 : 500, color: isToday(d) ? color : '#1e293b' }}>{d}</span>
                  {hasEvents && (
                    <div style={{ display: 'flex', gap: '2px', marginTop: '1px' }}>
                      {byDay[d].slice(0, 3).map((a, j) => (
                        <div key={j} style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: isReg ? '#22c55e' : (a.activity_type_color || color) }} />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Légende */}
      <div style={{ display: 'flex', gap: '12px', marginTop: '10px', fontSize: '0.68rem', color: '#64748b' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: color, display: 'inline-block' }}/> Événement</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#22c55e', display: 'inline-block' }}/> Inscrit</span>
      </div>

      {/* Popup événements du jour */}
      {popup && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '16px' }}
          onClick={() => setPopup(null)}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '18px', width: '100%', maxWidth: '340px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div style={{ fontWeight: 700, color: '#1e293b' }}>{popup.day} {MONTHS_FR[month]}</div>
              <button onClick={() => setPopup(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '1.2rem' }}>×</button>
            </div>
            {popup.events.map(a => (
              <div key={a.id} style={{ padding: '10px 12px', backgroundColor: '#f8fafc', borderRadius: '8px', marginBottom: '8px', borderLeft: `4px solid ${a.activity_type_color || color}` }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>{a.name}</div>
                {a.start_time && <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '3px' }}>{a.start_time.slice(0,5)}{a.end_time ? ` — ${a.end_time.slice(0,5)}` : ''} · {a.location}</div>}
                {registeredIds.has(a.id) && <div style={{ fontSize: '0.73rem', color: '#16a34a', fontWeight: 700, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg> Vous êtes inscrit(e)</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MODAL CHANGEMENT MOT DE PASSE
═══════════════════════════════════════════════════════════════════════════ */
function ChangePasswordModal({ participant, onClose }) {
  const [pwdErrors, setPwdErrors] = useState({});
  const [oldPwd, setOldPwd]       = useState('');
  const [newPwd, setNewPwd]       = useState('');
  const [confirmPwd, setConfirm]  = useState('');
  const [showOld, setShowOld]     = useState(false);
  const [showNew, setShowNew]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const hasPassword = participant.has_password;

  const handle = async (e) => {
    e.preventDefault();
    const errs = {};
    if (hasPassword && !oldPwd) errs.old = requiredMessage('Mot de passe actuel');
    if (!newPwd) errs.new = requiredMessage('Nouveau mot de passe');
    else if (newPwd.length < 4) errs.new = 'Le mot de passe doit contenir au moins 4 caractères.';
    if (!confirmPwd) errs.confirm = requiredMessage('Confirmer le mot de passe');
    else if (newPwd && newPwd !== confirmPwd) errs.confirm = 'Les mots de passe ne correspondent pas.';
    setPwdErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true); setError('');
    try {
      await api.post(`/participants/fidele/change-password/`, {
        qr_code: participant.qr_code,
        old_password: oldPwd,
        new_password: newPwd,
      });
      setSuccess('Mot de passe mis à jour !');
      setTimeout(onClose, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la mise à jour.');
    } finally { setSaving(false); }
  };

  const PwdInput = ({ value, onChange, show, onToggle, placeholder }) => (
    <div style={{ position: 'relative' }}>
      <input type={show ? 'text' : 'password'} style={{ ...s.input, paddingRight: '44px' }}
        value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
      <button type="button" onClick={onToggle}
        style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
        {show
          ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
          : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
        }
      </button>
    </div>
  );

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: '1.05rem', color: '#1e293b' }}>
              {hasPassword ? 'Modifier le mot de passe' : 'Créer un mot de passe'}
            </div>
            <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '2px' }}>
              {hasPassword ? 'Entrez l\'ancien puis le nouveau' : 'Sécurisez votre espace membre'}
            </div>
          </div>
          <button style={s.btnClose} onClick={onClose}><XIcon size={17}/></button>
        </div>

        {success ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <div style={{ fontWeight: 700, color: '#15803d' }}>{success}</div>
          </div>
        ) : (
          <form onSubmit={handle} noValidate style={{ display: 'flex', flexDirection: 'column', gap: '13px' }}>
            {error && <Err msg={error} />}
            {hasPassword && (
              <Field label="Mot de passe actuel *" error={pwdErrors.old}>
                <PwdInput value={oldPwd} onChange={setOldPwd} show={showOld} onToggle={() => setShowOld(v => !v)} placeholder="Ancien mot de passe" />
              </Field>
            )}
            <Field label="Nouveau mot de passe *" error={pwdErrors.new}>
              <PwdInput value={newPwd} onChange={setNewPwd} show={showNew} onToggle={() => setShowNew(v => !v)} placeholder="Minimum 4 caractères" />
            </Field>
            <Field label="Confirmer le mot de passe *" error={pwdErrors.confirm}>
              <input type="password" style={{ ...s.input, borderColor: pwdErrors.confirm ? ERROR_COLOR : undefined }} value={confirmPwd} onChange={e => setConfirm(e.target.value)} placeholder="Retaper le mot de passe" />
            </Field>
            <div style={{ display: 'flex', gap: '9px', marginTop: '4px' }}>
              <button type="button" style={{ ...s.btnSecondary, flex: 1 }} onClick={onClose}>Annuler</button>
              <button type="submit" style={{ ...s.btnPrimary, flex: 2, backgroundColor: '#7c3aed' }} disabled={saving}>
                {saving ? 'Enregistrement...' : hasPassword ? 'Mettre à jour' : 'Créer le mot de passe'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DASHBOARD FIDÈLE (membre connecté)
═══════════════════════════════════════════════════════════════════════════ */
function MembershipRequestForm({ participant, onClose, onSubmitted }) {
  const MARITAL_OPTS = [
    { value: '', label: 'Sélectionner' },
    { value: 'celibataire', label: 'Célibataire' },
    { value: 'marie', label: 'Marié(e)' },
    { value: 'divorce', label: 'Divorcé(e)' },
    { value: 'veuf', label: 'Veuf/Veuve' },
  ];
  const [form, setForm] = useState({
    motivation: '',
    baptised: false,
    baptism_date: '',
    previous_church: '',
    birth_date: participant.birth_date || '',
    phone: participant.phone || '',
    address: participant.address || '',
    city: participant.city || '',
    profession: participant.profession || '',
    marital_status: participant.marital_status || '',
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.motivation.trim()) errs.motivation = requiredMessage('Motivation');
    if (!form.phone.trim()) errs.phone = requiredMessage('Téléphone');
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSaving(true);
    try {
      await api.post(`/participants/fidele/membership-request/`, {
        qr_code: participant.qr_code,
        ...form,
      });
      onSubmitted();
    } catch (err) {
      const data = err.response?.data;
      if (data?.error) setErrors({ global: data.error });
      else setErrors({ global: 'Une erreur est survenue.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#1e293b' }}>Demande d'adhésion</div>
            <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>Remplissez ce formulaire pour devenir membre</div>
          </div>
          <button style={s.btnClose} onClick={onClose}><XIcon size={16}/></button>
        </div>

        {errors.global && <Err msg={errors.global} />}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '13px', marginTop: '12px' }}>
          <Field label="Pourquoi souhaitez-vous devenir membre ? *" error={errors.motivation}>
            <textarea
              style={{ ...s.input, minHeight: '80px', resize: 'vertical', borderColor: errors.motivation ? '#ef4444' : '#d1d5db' }}
              value={form.motivation}
              onChange={e => setForm({ ...form, motivation: e.target.value })}
              placeholder="Décrivez votre motivation..."
            />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '11px' }}>
            <Field label="Téléphone *" error={errors.phone}>
              <input style={{ ...s.input, borderColor: errors.phone ? '#ef4444' : '#d1d5db' }}
                value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="6XX XXX XXX" />
            </Field>
            <Field label="Date de naissance">
              <input type="date" style={s.input}
                value={form.birth_date} onChange={e => setForm({ ...form, birth_date: e.target.value })} />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '11px' }}>
            <Field label="Ville">
              <input style={s.input} value={form.city}
                onChange={e => setForm({ ...form, city: e.target.value })} placeholder="Votre ville" />
            </Field>
            <Field label="Situation familiale">
              <select style={{ ...s.input, backgroundColor: 'white' }}
                value={form.marital_status} onChange={e => setForm({ ...form, marital_status: e.target.value })}>
                {MARITAL_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Adresse">
            <input style={s.input} value={form.address}
              onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Votre adresse" />
          </Field>

          <Field label="Profession">
            <input style={s.input} value={form.profession}
              onChange={e => setForm({ ...form, profession: e.target.value })} placeholder="Votre profession" />
          </Field>

          <Field label="Église précédente (facultatif)">
            <input style={s.input} value={form.previous_church}
              onChange={e => setForm({ ...form, previous_church: e.target.value })} placeholder="Nom de l'église" />
          </Field>

          <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
            <input type="checkbox" id="baptised" checked={form.baptised}
              onChange={e => setForm({ ...form, baptised: e.target.checked })} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
            <label htmlFor="baptised" style={{ fontSize: '0.9rem', color: '#374151', cursor: 'pointer' }}>J'ai été baptisé(e)</label>
          </div>

          {form.baptised && (
            <Field label="Date de baptême">
              <input type="date" style={s.input}
                value={form.baptism_date} onChange={e => setForm({ ...form, baptism_date: e.target.value })} />
            </Field>
          )}

          <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fef08a', borderRadius: '8px', padding: '10px 13px', fontSize: '0.82rem', color: '#92400e' }}>
            Votre demande sera examinée par un responsable qui vous contactera. Le statut vous sera communiqué sur cette page.
          </div>

          <div style={{ display: 'flex', gap: '9px', marginTop: '4px' }}>
            <button type="button" style={s.btnSecondary} onClick={onClose}>Annuler</button>
            <button type="submit" style={{ ...s.btnPrimary, flex: 1, backgroundColor: '#7c3aed' }} disabled={saving}>
              {saving ? 'Envoi...' : 'Soumettre ma demande'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ participant: initialParticipant, onLogout, onParticipantUpdate, selectedChurch }) {
  const [participant, setParticipant] = useState(initialParticipant);
  const [activities, setActs]      = useState([]);
  const [myRegs, setMyRegs]        = useState([]);
  const [history, setHistory]      = useState([]);
  const [tab, setTab]              = useState('activities');
  const [showCard, setShowCard]    = useState(!!initialParticipant._visitor_activity);
  const [showScanner, setShowScanner] = useState(false);
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [selected, setSelected]    = useState(null);
  const [actDetail, setActDetail]  = useState(null); // modale détail activité
  const [unregTarget, setUnregTarget] = useState(null);
  const [successMsg, setOk]        = useState('');
  const [loadA, setLoadA]          = useState(false);
  const [actError, setActError]    = useState(false);
  // Filtre + pagination onglet activités
  const [actSearch, setActSearch]  = useState('');
  // 'all' par défaut : une activité « En cours » (le jour même) doit être visible sans manipuler les filtres
  const [actFilter, setActFilter]  = useState('all'); // all | planned | ongoing
  const [actDateFrom, setActDateFrom] = useState('');
  const [actDateTo, setActDateTo]     = useState('');
  const [actType, setActType]      = useState('');       // activity_type name
  const [actPage, setActPage]      = useState(1);
  const ACT_PAGE_SIZE = 4;
  const [loadH, setLoadH]          = useState(false);
  const [loadR, setLoadR]          = useState(false);
  // Membership request
  const [memberReqStatus, setMemberReqStatus] = useState(null);
  const [showMemberReqForm, setShowMemberReqForm] = useState(false);
  // Photo upload
  const photoInputRef = useRef(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  // Church settings (nom, logo)
  const [churchSettings, setChurchSettings] = useState({ church_name: CHURCH_NAME, logo: null });
  // Édition du profil
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editChurches, setEditChurches] = useState([]);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  // Fidèles en ligne
  const [onlineCount, setOnlineCount] = useState(null);
  // Notifications push
  const push = usePushNotifications({ churchId: selectedChurch?.id, participantId: participant.id });

  useEffect(() => {
    if (!selectedChurch?.id) return;
    const churchId = selectedChurch.id;
    const fetchOnline = () => {
      api.get(`/participants/public/count/?church_id=${churchId}`)
        .then(r => setOnlineCount(r.data?.count ?? null))
        .catch(() => {});
    };
    fetchOnline();
    const interval = setInterval(fetchOnline, 60000);
    return () => clearInterval(interval);
  }, [selectedChurch?.id]); // eslint-disable-line

  useEffect(() => {
    const url = selectedChurch?.id
      ? `/notifications/church-settings/?church_id=${selectedChurch.id}`
      : `/notifications/church-settings/`;
    api.get(url)
      .then(r => setChurchSettings(r.data))
      .catch(() => {});
  }, [selectedChurch?.id]);

  // Centre de notifications : dernier ID vu stocké en localStorage (pas de
  // compte "lu/non lu" côté serveur pour un fidèle sans session admin) —
  // suffisant pour afficher un badge "nouvelles notifications" fiable.
  const [notifications, setNotifications] = useState([]);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const lastSeenKey = `fidele_last_seen_notif_${participant.qr_code}`;
  useEffect(() => {
    if (!participant.qr_code) return;
    const fetchNotifications = () => {
      api.get(`/notifications/fidele/notifications/${participant.qr_code}/`)
        .then(r => setNotifications(Array.isArray(r.data) ? r.data : []))
        .catch(() => {});
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [participant.qr_code]);
  const lastSeenId = Number(localStorage.getItem(lastSeenKey) || 0);
  const unreadCount = notifications.filter(n => n.id > lastSeenId).length;
  const openNotifPanel = () => {
    setShowNotifPanel(true);
    if (notifications.length > 0) localStorage.setItem(lastSeenKey, String(notifications[0].id));
  };


  const colorFrom = churchSettings.colors?.[participant.status]?.from || MEMBER_COLORS[participant.status] || '#000000';
  const colorTo   = churchSettings.colors?.[participant.status]?.to   || colorFrom;
  const color     = colorFrom;
  const isVisitor = participant.status === 'invite';
  const profileIncomplete = !participant.church;

  // IDs des activités où le fidèle est inscrit (Set pour lookup O(1))
  const registeredIds = new Set(myRegs.map(r => r.activity_id));

  // IDs des activités où le fidèle était réellement présent
  const attendedActivityIds = new Set(history.map(h => h.activity_id));

  // Retourne true si une inscription est pour une activité dont l'heure est dépassée
  const isRegPast = (reg) => {
    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);
    if (!reg.activity_date) return false;
    if (reg.activity_date < todayStr) return true;
    if (reg.activity_date === todayStr && reg.end_time) {
      const [h, m] = reg.end_time.split(':');
      const endDt = new Date();
      endDt.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
      return now >= endDt;
    }
    return false;
  };

  // Calcule la prochaine date d'occurrence pour une activité récurrente
  const getNextOccurrence = (act) => {
    if (!act.is_recurring || !act.recurrence_days?.length) return act.date;
    const today = new Date(); today.setHours(0,0,0,0);
    const start = new Date(act.date);
    const end = act.recurrence_end ? new Date(act.recurrence_end) : new Date(today.getTime() + 2*365*24*60*60*1000);
    const from = start > today ? start : today;
    for (let d = new Date(from); d <= end; d.setDate(d.getDate() + 1)) {
      const idx = d.getDay() === 0 ? 6 : d.getDay() - 1;
      if (act.recurrence_days.includes(idx)) {
        return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      }
    }
    return null;
  };

  // Enrichit les activités avec leur prochaine date d'occurrence et filtre celles terminées
  const allDisplayActivities = activities
    .map(act => ({ ...act, nextDate: getNextOccurrence(act) }))
    .filter(act => act.nextDate !== null)
    .sort((a, b) => (a.nextDate > b.nextDate ? 1 : -1));

  // Filtre recherche + statut + période + type
  const filteredActivities = allDisplayActivities
    .filter(act => {
      const matchSearch = !actSearch || act.name.toLowerCase().includes(actSearch.toLowerCase()) || (act.location || '').toLowerCase().includes(actSearch.toLowerCase()) || (act.activity_type || '').toLowerCase().includes(actSearch.toLowerCase());
      const matchFilter = actFilter === 'all' || act.status === actFilter;
      const matchType   = !actType || act.activity_type === actType;
      const d = act.nextDate || act.date || '';
      const matchFrom = !actDateFrom || d >= actDateFrom;
      const matchTo   = !actDateTo   || d <= actDateTo;
      return matchSearch && matchFilter && matchType && matchFrom && matchTo;
    });

  // Pagination
  const totalPages = Math.ceil(filteredActivities.length / ACT_PAGE_SIZE);
  const displayActivities = filteredActivities.slice((actPage - 1) * ACT_PAGE_SIZE, actPage * ACT_PAGE_SIZE);

  // Le périmètre (église du fidèle) est résolu côté serveur à partir du qr_code
  const fetchActivities = () => {
    setLoadA(true);
    setActError(false);
    api.get(`/participants/fidele/activities/?qr_code=${participant.qr_code}`, { silentError: true })
      .then(r => setActs(Array.isArray(r.data) ? r.data : []))
      .catch(() => { setActs([]); setActError(true); })
      .finally(() => setLoadA(false));
  };

  const fetchMyRegs = () => {
    setLoadR(true);
    api.get(`/participants/fidele/my-registrations/?qr_code=${participant.qr_code}`)
      .then(r => setMyRegs(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setLoadR(false));
  };

  const fetchMemberReqStatus = () => {
    api.get(`/participants/fidele/membership-status/?qr_code=${participant.qr_code}`)
      .then(r => setMemberReqStatus(r.data))
      .catch(() => {});
  };

  // Relit le profil (sans redemander le mot de passe) ; si la fiche n'existe
  // plus (supprimée ou fusionnée), la session est fermée proprement.
  const refreshParticipant = () => {
    api.get(`/participants/fidele/profile/${participant.qr_code}/`, { silentError: true })
      .then(r => {
        const { history: _history, ...fresh } = r.data;
        const updated = { ...participant, ...fresh };
        setParticipant(updated);
        if (onParticipantUpdate) onParticipantUpdate(updated);
      })
      .catch(err => { if (err.response?.status === 404) onLogout(); });
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('Image trop lourde (max 5 Mo).'); return; }
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('qr_code', participant.qr_code);
      fd.append('photo', file);
      const res = await api.post(`/participants/fidele/upload-photo/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const updated = { ...participant, photo_url: res.data.photo_url };
      setParticipant(updated);
      if (onParticipantUpdate) onParticipantUpdate(updated);
    } catch {
      alert("Erreur lors de l'upload.");
    } finally {
      setPhotoUploading(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = async () => {
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('qr_code', participant.qr_code);
      fd.append('remove', 'true');
      await api.post(`/participants/fidele/upload-photo/`, fd);
      const updated = { ...participant, photo_url: null };
      setParticipant(updated);
      if (onParticipantUpdate) onParticipantUpdate(updated);
    } catch {
      alert("Erreur lors de la suppression.");
    } finally {
      setPhotoUploading(false);
    }
  };

  const openEditProfile = () => {
    setEditForm({
      first_name: '', last_name: '', phone: '', whatsapp: '',
      email: '', city: '', address: '', profession: '', church_id: '',
    });
    setEditError('');
    setShowEditProfile(true);
    if (profileIncomplete && editChurches.length === 0) {
      api.get(`/churches/public/`)
        .then(r => setEditChurches(Array.isArray(r.data) ? r.data : []))
        .catch(() => {});
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    // Build payload: only send fields that were actually filled in
    const payload = {};
    Object.entries(editForm).forEach(([key, val]) => {
      if (val.trim() !== '') payload[key] = val.trim();
    });
    if (Object.keys(payload).length === 0) {
      setEditError('Modifiez au moins un champ avant d\'enregistrer.');
      return;
    }
    setEditSaving(true);
    setEditError('');
    try {
      const res = await api.patch(
        `/participants/fidele/update-profile/`,
        { qr_code: participant.qr_code, ...payload },
        { headers: { 'Content-Type': 'application/json' } }
      );
      const updated = { ...participant, ...res.data };
      setParticipant(updated);
      if (onParticipantUpdate) onParticipantUpdate(updated);
      setShowEditProfile(false);
      if (payload.church_id) fetchActivities();
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === 'object') {
        const msgs = Object.values(data).flat().join(' ');
        setEditError(msgs || 'Erreur lors de la sauvegarde.');
      } else {
        setEditError('Erreur lors de la sauvegarde.');
      }
    } finally {
      setEditSaving(false);
    }
  };

  useEffect(() => {
    fetchActivities();
    fetchMyRegs();
    if (isVisitor) fetchMemberReqStatus();
    // Refresh silencieux pour récupérer les données à jour (photo, infos) après rechargement
    refreshParticipant();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Quand le statut de la demande est approuvé, recharger le participant
  useEffect(() => {
    if (memberReqStatus?.status === 'approved') {
      refreshParticipant();
    }
  }, [memberReqStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Charger au démarrage + à chaque fois que l'onglet history est affiché
    if (tab !== 'history' && history.length > 0) return; // déjà chargé, pas besoin de recharger
    setLoadH(true);
    api.get(`/participants/fidele/profile/${participant.qr_code}/`)
      .then(r => setHistory(r.data.history || []))
      .catch(() => {})
      .finally(() => setLoadH(false));
  }, [tab, participant.qr_code]); // eslint-disable-line react-hooks/exhaustive-deps

  // Se désinscrire d'une activité
  const handleUnregister = async (reg) => {
    try {
      await api.delete(`/participants/fidele/register/`, {
        data: { qr_code: participant.qr_code, activity_id: reg.activity_id }
      });
      setOk(`Désinscription de « ${reg.activity_name} » confirmée.`);
      setUnregTarget(null);
      fetchMyRegs();
      fetchActivities();
    } catch (err) {
      setOk(err.response?.data?.error || 'Erreur lors de la désinscription.');
    }
  };

  const initials = `${participant.first_name?.[0] || ''}${participant.last_name?.[0] || ''}`.toUpperCase();

  // ─── Navigation tabs config ───────────────────────────────────────────────
  const tabs = [
    { key: 'activities', label: 'Activités',
      icon: (active) => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? color : '#94a3b8'} strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
    },
    { key: 'calendar', label: 'Calendrier',
      icon: (active) => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? color : '#94a3b8'} strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
    },
    { key: 'myregs', label: isVisitor ? 'Inscriptions' : 'Inscriptions',
      icon: (active) => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? color : '#94a3b8'} strokeWidth="2"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
      badge: myRegs.length > 0 ? myRegs.length : null,
    },
    { key: 'history', label: 'Historique',
      icon: (active) => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? color : '#94a3b8'} strokeWidth="2"><path d="M3 3v5h5"/><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/></svg>,
    },
    { key: 'gallery', label: 'Galerie',
      icon: (active) => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? color : '#94a3b8'} strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>,
    },
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f1f5f9', fontFamily: 'system-ui,-apple-system,sans-serif' }}>
      {/* Centering wrapper for desktop */}
      <div style={{ maxWidth: '520px', margin: '0 auto', position: 'relative' }}>

      {/* ── HERO HEADER ─────────────────────────────────────────────────── */}
      <div style={{
        background: `linear-gradient(135deg, ${colorFrom} 0%, ${colorTo} 100%)`,
        padding: '0 0 20px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Cercles décoratifs */}
        <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '160px', height: '160px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)', pointerEvents: 'none' }}/>
        <div style={{ position: 'absolute', bottom: '-20px', left: '-30px', width: '110px', height: '110px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }}/>

        {/* Top bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {(selectedChurch?.logo_url || churchSettings.logo)
              ? <img src={selectedChurch?.logo_url || churchSettings.logo} alt="logo" style={{ width: '28px', height: '28px', objectFit: 'contain', borderRadius: '6px', opacity: 0.9 }} />
              : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="1.8"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            }
            <div>
              <span style={{ fontWeight: 700, color: 'rgba(255,255,255,0.9)', fontSize: '0.92rem', display: 'block', lineHeight: 1.2 }}>{selectedChurch?.name || churchSettings.church_name}</span>
              {selectedChurch?.pastor && <span style={{ fontSize: '0.67rem', color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>Pasteur {selectedChurch.pastor}</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button onClick={openNotifPanel} title="Notifications"
              style={{ position: 'relative', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '10px', padding: '7px 9px', color: 'rgba(255,255,255,0.9)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
              {unreadCount > 0 && (
                <span style={{ position: 'absolute', top: -3, right: -3, background: '#ef4444', color: '#fff', fontSize: '0.6rem', fontWeight: 800, borderRadius: '999px', minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            <button onClick={() => setShowCard(true)}
              style={{ background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '10px', padding: '7px 12px', color: 'white', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '5px' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
              Ma carte
            </button>
            <button onClick={onLogout}
              style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '10px', padding: '7px 12px', color: 'rgba(255,255,255,0.85)', cursor: 'pointer', fontSize: '0.78rem' }}>
              Déco.
            </button>
          </div>
        </div>

        {/* Profile section in hero */}
        <div style={{ padding: '18px 18px 0', display: 'flex', alignItems: 'center', gap: '14px' }}>
          {/* Avatar */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div
              onClick={() => !photoUploading && photoInputRef.current?.click()}
              style={{
                width: '70px', height: '70px', borderRadius: '20px',
                background: participant.photo_url ? 'none' : 'rgba(255,255,255,0.25)',
                border: '3px solid rgba(255,255,255,0.6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden', cursor: 'pointer',
                boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
              }}>
              {participant.photo_url
                ? <img src={participant.photo_url} alt="profil" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <span style={{ fontSize: '1.5rem', fontWeight: 800, color: 'white' }}>{initials}</span>
              }
            </div>
            <div
              onClick={() => !photoUploading && photoInputRef.current?.click()}
              style={{ position: 'absolute', bottom: '-4px', right: '-4px', width: '22px', height: '22px', borderRadius: '50%', backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', border: `2px solid ${color}` }}>
              {photoUploading
                ? <div style={{ width: '8px', height: '8px', borderRadius: '50%', border: `2px solid ${color}`, borderTopColor: 'transparent', animation: 'spin 0.6s linear infinite' }} />
                : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
              }
            </div>
            <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} />
          </div>

          {/* Name + status + code */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '1.15rem', fontWeight: 900, color: 'white', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {participant.first_name} {participant.last_name}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginTop: '5px', flexWrap: 'wrap' }}>
              <span style={{ backgroundColor: 'rgba(255,255,255,0.22)', color: 'white', borderRadius: '999px', padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700 }}>
                {STATUS_LABELS[participant.status]}
              </span>
              {churchSettings.use_families && participant.family_name && (
                <span style={{ backgroundColor: 'rgba(255,255,255,0.22)', color: 'white', borderRadius: '999px', padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700 }}>
                  👪 {participant.family_name}
                </span>
              )}
              {!isVisitor && (
                <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255,255,255,0.75)', letterSpacing: '0.08em' }}>
                  {participant.member_code}
                </span>
              )}
            </div>
            {/* Église + pasteur + fidèles en ligne */}
            <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '1px' }}>
              <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.75)', fontWeight: 600 }}>
                🏛 {selectedChurch?.name || churchSettings.church_name}
              </span>
              {selectedChurch?.pastor && (
                <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.6)' }}>
                  ✝ Pasteur {selectedChurch.pastor}
                </span>
              )}
              {onlineCount !== null && (
                <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#4ade80', display: 'inline-block', boxShadow: '0 0 6px #4ade80' }} />
                  {onlineCount} fidèle{onlineCount > 1 ? 's' : ''} enregistré{onlineCount > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>

          {/* QR mini */}
          {!isVisitor && (
            <div style={{ backgroundColor: 'white', borderRadius: '10px', padding: '5px', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', flexShrink: 0 }}>
              <QRCodeSVG value={participant.qr_code || participant.member_code} size={44} fgColor={color} level="M"/>
            </div>
          )}
        </div>
      </div>

      {/* ── STATS CARDS ─────────────────────────────────────────────────── */}
      <div style={{ background: `linear-gradient(180deg, ${color}cc 0%, #f1f5f9 60%)`, padding: '12px 14px 0' }}>
        {isVisitor ? (
          <div style={{ backgroundColor: 'white', borderRadius: '14px', padding: '14px 18px', boxShadow: '0 4px 16px rgba(0,0,0,0.10)', display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '14px' }}>
            <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CalendarIcon size={18} color={color}/>
            </div>
            <div>
              <div style={{ fontSize: '1.4rem', fontWeight: 900, color: '#1e293b' }}>{activities.length}</div>
              <div style={{ fontSize: '0.72rem', color: '#64748b' }}>Activités disponibles</div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '14px' }}>
            {[
              { label: 'Présences', value: participant.attendance_count ?? 0, icon: <UsersIcon size={18} color={color}/> },
              { label: 'Activités', value: activities.length, icon: <CalendarIcon size={18} color={color}/> },
              { label: STATUS_LABELS[participant.status]?.split(' ')[0], value: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill={color} stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
              ), icon: null },
            ].map((st, i) => (
              <div key={i} style={{ backgroundColor: 'white', borderRadius: '14px', padding: '12px 8px 10px', boxShadow: '0 4px 16px rgba(0,0,0,0.08)', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                {st.icon && (
                  <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: `${color}14`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {st.icon}
                  </div>
                )}
                <div style={{ fontSize: i === 2 ? '1rem' : '1.5rem', fontWeight: 900, color: '#1e293b', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{st.value}</div>
                <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 600 }}>{st.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── SCROLLABLE CONTENT ──────────────────────────────────────────── */}
      <div style={{ padding: '0 16px 130px' }}>

        {successMsg && (
          <div style={{ ...s.successBox, marginBottom: '14px' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            {successMsg}
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', marginLeft: 'auto' }} onClick={() => setOk('')}><XIcon size={13} color="#166534"/></button>
          </div>
        )}

        {/* Bannière "Devenir membre" */}
        {isVisitor && !memberReqStatus?.exists && (
          <div style={{ background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', borderRadius: '16px', padding: '16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '12px', padding: '10px', flexShrink: 0 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                <line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: '0.9rem', color: 'white' }}>Rejoignez l'église</div>
              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.75)', marginTop: '2px' }}>Soumettez une demande d'adhésion</div>
            </div>
            <button style={{ backgroundColor: 'white', color: '#7c3aed', border: 'none', borderRadius: '10px', padding: '9px 14px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 800, flexShrink: 0 }}
              onClick={() => setShowMemberReqForm(true)}>
              Postuler
            </button>
          </div>
        )}

        {/* Statut demande en cours */}
        {isVisitor && memberReqStatus?.exists && (
          <div style={{
            backgroundColor: memberReqStatus.status === 'approved' ? '#f0fdf4' : memberReqStatus.status === 'rejected' ? '#fef2f2' : '#fffbeb',
            border: `1.5px solid ${memberReqStatus.status === 'approved' ? '#bbf7d0' : memberReqStatus.status === 'rejected' ? '#fecaca' : '#fef08a'}`,
            borderRadius: '14px', padding: '14px 16px', marginBottom: '16px', display: 'flex', gap: '10px', alignItems: 'flex-start',
          }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', marginTop: '5px', flexShrink: 0, backgroundColor: memberReqStatus.status === 'approved' ? '#22c55e' : memberReqStatus.status === 'rejected' ? '#ef4444' : '#f59e0b' }}/>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: memberReqStatus.status === 'approved' ? '#15803d' : memberReqStatus.status === 'rejected' ? '#b91c1c' : '#92400e' }}>
                {memberReqStatus.status === 'pending' && 'Demande en attente de validation'}
                {memberReqStatus.status === 'approved' && 'Demande approuvée !'}
                {memberReqStatus.status === 'rejected' && 'Demande non retenue'}
              </div>
              <button style={{ marginTop: '4px', background: 'none', border: 'none', fontSize: '0.73rem', color: '#64748b', cursor: 'pointer', padding: 0 }} onClick={fetchMemberReqStatus}>Actualiser</button>
            </div>
          </div>
        )}

        {/* ── Bouton Scanner flottant ── */}
        <button
          onClick={() => setShowScanner(true)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
            padding: '15px', marginBottom: '18px',
            background: `linear-gradient(135deg, ${colorFrom}, ${colorTo})`,
            color: 'white', border: 'none', borderRadius: '16px', cursor: 'pointer',
            fontWeight: 800, fontSize: '0.97rem',
            boxShadow: `0 8px 24px ${color}55`,
          }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/>
            <path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/>
            <rect x="7" y="7" width="3" height="3"/><rect x="14" y="7" width="3" height="3"/>
            <rect x="7" y="14" width="3" height="3"/><rect x="14" y="14" width="3" height="3"/>
          </svg>
          Scanner le QR — Marquer ma présence
        </button>

        {/* ── Actions rapides (profil, photo, mdp) ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '18px' }}>
          {[
            { label: 'Modifier profil', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>, onClick: openEditProfile },
            { label: participant.photo_url ? 'Retirer photo' : 'Ajouter photo', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>, onClick: () => participant.photo_url ? handleRemovePhoto() : (!photoUploading && photoInputRef.current?.click()) },
            { label: participant.has_password ? 'Mot de passe' : 'Créer mdp', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>, onClick: () => setShowChangePwd(true) },
          ].map((a, i) => (
            <button key={i} onClick={a.onClick}
              style={{ backgroundColor: 'white', border: `1.5px solid ${color}20`, borderRadius: '14px', padding: '13px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '7px', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700, color: '#374151', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <div style={{ width: '38px', height: '38px', borderRadius: '11px', background: `${color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{a.icon}</div>
              {a.label}
            </button>
          ))}
        </div>

        {/* ── Bannières : rappel activités à venir + activités manquées ── */}
        {(() => {
          const todayStr = new Date().toISOString().slice(0, 10);
          const todayRegs = myRegs.filter(reg => {
            if (reg.activity_date === todayStr) return true;
            const act = activities.find(a => a.id === reg.activity_id);
            if (!act?.is_recurring || !act.recurrence_days?.length) return false;
            const todayIdx = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1;
            const endOk = !act.recurrence_end || act.recurrence_end >= todayStr;
            return act.recurrence_days.includes(todayIdx) && endOk;
          });
          const upcoming = todayRegs.filter(r => !isRegPast(r));
          const missed   = myRegs.filter(r => isRegPast(r) && !attendedActivityIds.has(r.activity_id));
          return (
            <>
              {/* Rappel activités du jour pas encore passées */}
              {upcoming.length > 0 && (
                <div style={{ background: 'linear-gradient(135deg, #1e40af, #4f46e5)', borderRadius: '14px', padding: '14px 16px', marginBottom: '14px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: 'white', fontWeight: 800, fontSize: '0.92rem', marginBottom: '4px' }}>Rappel — Activité aujourd'hui !</div>
                    {upcoming.map(reg => (
                      <div key={reg.record_id} style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.82rem', marginBottom: '2px' }}>
                        📍 <strong>{reg.activity_name}</strong>{reg.start_time ? ` à ${reg.start_time.slice(0,5)}` : ''} · {reg.location || ''}
                      </div>
                    ))}
                    <div style={{ marginTop: '10px', background: 'rgba(255,255,255,0.15)', borderRadius: '8px', padding: '8px 12px', color: 'white', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '7px' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/><path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/><rect x="7" y="7" width="3" height="3"/><rect x="14" y="14" width="3" height="3"/></svg>
                      N'oubliez pas de <strong style={{ marginLeft: '3px' }}>scanner le QR</strong> à votre arrivée
                    </div>
                  </div>
                </div>
              )}
              {/* Activités manquées (inscrit mais absent) */}
              {missed.length > 0 && (
                <div style={{ background: 'linear-gradient(135deg, #7f1d1d, #b91c1c)', borderRadius: '14px', padding: '14px 16px', marginBottom: '14px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: 'white', fontWeight: 800, fontSize: '0.92rem', marginBottom: '6px' }}>
                      {missed.length > 1 ? `Vous avez manqué ${missed.length} activités` : 'Vous avez manqué une activité'}
                    </div>
                    {missed.map(reg => (
                      <div key={reg.record_id} style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.82rem', marginBottom: '3px' }}>
                        📅 <strong>{reg.activity_name}</strong>{reg.activity_date ? ` — ${new Date(reg.activity_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long' })}` : ''}
                      </div>
                    ))}
                    <div style={{ marginTop: '8px', fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' }}>
                      Votre présence n'a pas été enregistrée pour ces activités.
                    </div>
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {/* ── Tab content ── */}

        {/* Onglet : Activités */}
        {tab === 'activities' && (
          <div>
            {/* ── Barre de filtre complète ── */}
            <div style={{ marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>

              {/* Recherche */}
              <div style={{ position: 'relative' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input
                  value={actSearch}
                  onChange={e => { setActSearch(e.target.value); setActPage(1); }}
                  placeholder="Rechercher par nom, lieu, type…"
                  style={{ width:'100%', padding:'10px 12px 10px 36px', borderRadius:12, border:'1.5px solid #e2e8f0', fontSize:'0.88rem', outline:'none', background:'white', boxSizing:'border-box' }}
                />
                {actSearch && (
                  <button onClick={() => { setActSearch(''); setActPage(1); }} style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#94a3b8', fontSize:'1rem', lineHeight:1 }}>×</button>
                )}
              </div>

              {/* Filtres statut — pills */}
              <div style={{ display:'flex', gap:6, overflowX:'auto', paddingBottom:2 }}>
                {[
                  { key:'all',     label:`Tout (${allDisplayActivities.length})` },
                  { key:'planned', label:'À venir' },
                  { key:'ongoing', label:'En cours' },
                ].map(f => (
                  <button key={f.key} onClick={() => { setActFilter(f.key); setActPage(1); }}
                    style={{ padding:'6px 13px', borderRadius:999, border: actFilter===f.key?'none':'1px solid #e2e8f0', cursor:'pointer', fontSize:'0.78rem', fontWeight:700, background:actFilter===f.key?color:'white', color:actFilter===f.key?'white':'#64748b', whiteSpace:'nowrap', transition:'all .15s', flexShrink:0 }}>
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Dates + Type */}
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {/* Plage de dates */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                  <div>
                    <label style={{ display:'block', fontSize:'0.7rem', fontWeight:600, color:'#94a3b8', marginBottom:4, textTransform:'uppercase', letterSpacing:'0.06em' }}>Du</label>
                    <input type="date" value={actDateFrom}
                      onChange={e => { setActDateFrom(e.target.value); setActPage(1); }}
                      style={{ width:'100%', padding:'8px 10px', borderRadius:10, border:'1px solid #e2e8f0', fontSize:'0.82rem', background:'white', outline:'none', color:'#374151', boxSizing:'border-box', accentColor: color }}
                    />
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:'0.7rem', fontWeight:600, color:'#94a3b8', marginBottom:4, textTransform:'uppercase', letterSpacing:'0.06em' }}>Au</label>
                    <input type="date" value={actDateTo}
                      onChange={e => { setActDateTo(e.target.value); setActPage(1); }}
                      min={actDateFrom || undefined}
                      style={{ width:'100%', padding:'8px 10px', borderRadius:10, border:'1px solid #e2e8f0', fontSize:'0.82rem', background:'white', outline:'none', color:'#374151', boxSizing:'border-box', accentColor: color }}
                    />
                  </div>
                </div>
                {/* Type */}
                <div>
                  <label style={{ display:'block', fontSize:'0.7rem', fontWeight:600, color:'#94a3b8', marginBottom:4, textTransform:'uppercase', letterSpacing:'0.06em' }}>Type d'activité</label>
                  <select value={actType} onChange={e => { setActType(e.target.value); setActPage(1); }}
                    style={{ width:'100%', padding:'8px 10px', borderRadius:10, border:'1px solid #e2e8f0', fontSize:'0.82rem', background:'white', cursor:'pointer', outline:'none', color: actType ? '#374151' : '#94a3b8' }}>
                    <option value="">Tous les types</option>
                    {[...new Set(allDisplayActivities.map(a => a.activity_type).filter(Boolean))].sort().map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Résultats + reset */}
              {(actSearch || actDateFrom || actDateTo || actType || actFilter !== 'all') && (
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', paddingTop:4 }}>
                  <span style={{ fontSize:'0.75rem', color:'#94a3b8' }}>
                    <strong style={{ color:'#374151' }}>{filteredActivities.length}</strong> résultat{filteredActivities.length > 1 ? 's' : ''}
                  </span>
                  <button onClick={() => { setActSearch(''); setActDateFrom(''); setActDateTo(''); setActType(''); setActFilter('all'); setActPage(1); }}
                    style={{ fontSize:'0.75rem', color:color, fontWeight:700, background:'none', border:'none', cursor:'pointer', padding:0, display:'flex', alignItems:'center', gap:4 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.51"/></svg>
                    Réinitialiser
                  </button>
                </div>
              )}
            </div>

            {/* ── Liste ── */}
            {loadA ? <Loader/> : filteredActivities.length === 0
              ? <ActivitiesEmpty
                  error={actError} incomplete={profileIncomplete} filtered={allDisplayActivities.length > 0}
                  search={actSearch} color={color} onRetry={fetchActivities} onComplete={openEditProfile} />
              : <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {displayActivities.map(act => {
                      const sc    = ACT_STATUS[act.status] || ACT_STATUS.planned;
                      const isReg = registeredIds.has(act.id);
                      const acColor = act.activity_type_color || color;
                      return (
                        <div key={act.id}
                          onClick={() => setActDetail(act)}
                          style={{ backgroundColor: 'white', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', cursor: 'pointer', transition: 'transform .15s, box-shadow .15s' }}
                          onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 6px 20px rgba(0,0,0,0.10)'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 2px 12px rgba(0,0,0,0.06)'; }}>
                          <div style={{ height: '4px', background: `linear-gradient(90deg,${acColor},${acColor}88)` }}/>
                          <div style={{ padding: '12px 14px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
                              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', flex: 1, lineHeight: 1.3 }}>
                                {act.name}
                                {act.is_global && <span style={{ marginLeft: '6px', fontSize: '0.62rem', padding: '2px 7px', background: '#eef2ff', color: '#3730a3', borderRadius: '999px', fontWeight: 700, verticalAlign: 'middle' }}>RCC Global</span>}
                              </div>
                              <span style={{ ...s.badge, backgroundColor: sc.bg, color: sc.text, flexShrink: 0 }}>{sc.label}</span>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                              <MetaRow icon={<CalendarIcon size={11} color="#94a3b8"/>}>{act.nextDate}</MetaRow>
                              {(act.start_time) && <MetaRow icon={<ClockIcon size={11} color="#94a3b8"/>}>{act.start_time.slice(0,5)}{act.end_time ? ` — ${act.end_time.slice(0,5)}` : ''}</MetaRow>}
                              {act.location && <MetaRow icon={<MapPinIcon size={11} color="#94a3b8"/>}>{act.location}</MetaRow>}
                              {act.activity_type && <span style={{ fontSize: '0.68rem', fontWeight: 600, color: acColor, backgroundColor: `${acColor}15`, borderRadius: '999px', padding: '2px 7px' }}>{act.activity_type}</span>}
                              {isReg && <span style={{ fontSize:'0.68rem', fontWeight:700, color:'#16a34a', background:'#dcfce7', borderRadius:999, padding:'2px 8px', display:'flex', alignItems:'center', gap:3 }}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>Inscrit</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* ── Pagination ── */}
                  {totalPages > 1 && (
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:16, paddingTop:14, borderTop:'1px solid #f1f5f9' }}>
                      <button onClick={() => setActPage(p => Math.max(1, p-1))} disabled={actPage===1}
                        style={{ display:'flex', alignItems:'center', gap:5, padding:'8px 14px', borderRadius:10, border:'1px solid #e2e8f0', background:'white', cursor:actPage===1?'default':'pointer', fontSize:'0.82rem', fontWeight:600, color:'#64748b', opacity:actPage===1?.4:1 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
                        Préc.
                      </button>
                      <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                        {Array.from({length: totalPages}, (_,i) => i+1)
                          .filter(p => p===1 || p===totalPages || Math.abs(p-actPage)<=1)
                          .reduce((acc, p, idx, arr) => {
                            if (idx > 0 && arr[idx-1] !== p-1) acc.push('…');
                            acc.push(p); return acc;
                          }, [])
                          .map((p, idx) => p === '…'
                            ? <span key={`d${idx}`} style={{ color:'#94a3b8', fontSize:'0.85rem' }}>…</span>
                            : <button key={p} onClick={() => setActPage(p)}
                                style={{ width:32, height:32, borderRadius:'50%', border:'none', cursor:'pointer', background:actPage===p?color:'transparent', color:actPage===p?'white':'#64748b', fontWeight:actPage===p?700:500, fontSize:'0.85rem' }}>
                                {p}
                              </button>
                          )
                        }
                      </div>
                      <button onClick={() => setActPage(p => Math.min(totalPages, p+1))} disabled={actPage===totalPages}
                        style={{ display:'flex', alignItems:'center', gap:5, padding:'8px 14px', borderRadius:10, border:'1px solid #e2e8f0', background:'white', cursor:actPage===totalPages?'default':'pointer', fontSize:'0.82rem', fontWeight:600, color:'#64748b', opacity:actPage===totalPages?.4:1 }}>
                        Suiv.
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                      </button>
                    </div>
                  )}
                </>
            }

          </div>
        )}

        {/* Onglet : Calendrier */}
        {tab === 'calendar' && (
          loadA ? <Loader/> :
          <MiniCalendar activities={activities} registeredIds={registeredIds} color={color}/>
        )}

        {/* Onglet : Mes inscriptions */}
        {tab === 'myregs' && (
          loadR ? <Loader/> : myRegs.length === 0
            ? <EmptyState icon={<svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>} text="Aucune inscription." />
            : <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {myRegs.map(reg => {
                  const sc = ACT_STATUS[reg.status] || ACT_STATUS.planned;
                  const rc = reg.activity_type_color || color;
                  const past = isRegPast(reg);
                  const attended = attendedActivityIds.has(reg.activity_id);
                  const missed = past && !attended;
                  return (
                    <div key={reg.record_id} style={{ backgroundColor: 'white', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.07)', border: missed ? '1.5px solid #fecaca' : '1.5px solid transparent' }}>
                      <div style={{ height: '4px', background: missed ? 'linear-gradient(90deg,#ef4444,#b91c1c)' : `linear-gradient(90deg,${rc},${rc}88)` }}/>
                      <div style={{ padding: '13px 16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '8px' }}>
                          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', flex: 1 }}>{reg.activity_name}</div>
                          {missed
                            ? <span style={{ fontSize: '0.72rem', fontWeight: 700, backgroundColor: '#fee2e2', color: '#b91c1c', borderRadius: '999px', padding: '2px 10px', flexShrink: 0 }}>Absent(e)</span>
                            : attended
                              ? <span style={{ fontSize: '0.72rem', fontWeight: 700, backgroundColor: '#dcfce7', color: '#16a34a', borderRadius: '999px', padding: '2px 10px', flexShrink: 0 }}>Présent(e)</span>
                              : <span style={{ ...s.badge, backgroundColor: sc.bg, color: sc.text, flexShrink: 0 }}>{sc.label}</span>
                          }
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px', marginBottom: '10px' }}>
                          <MetaRow icon={<CalendarIcon size={12} color="#94a3b8"/>}>{reg.activity_date}</MetaRow>
                          {reg.start_time && <MetaRow icon={<ClockIcon size={12} color="#94a3b8"/>}>{reg.start_time.slice(0,5)}{reg.end_time ? ` — ${reg.end_time.slice(0,5)}` : ''}</MetaRow>}
                          {reg.location && <MetaRow icon={<MapPinIcon size={12} color="#94a3b8"/>}>{reg.location}</MetaRow>}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          {missed ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.82rem', fontWeight: 700, color: '#b91c1c' }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                              Présence non enregistrée
                            </div>
                          ) : attended ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.82rem', fontWeight: 700, color: '#16a34a' }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                              Présence confirmée
                            </div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.82rem', fontWeight: 700, color: '#16a34a' }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                              Inscrit(e)
                            </div>
                          )}
                          {!past && (
                            <button style={{ padding: '7px 13px', borderRadius: '8px', border: '1px solid #fecaca', backgroundColor: '#fef2f2', color: '#b91c1c', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}
                              onClick={() => setUnregTarget(reg)}>
                              Se désinscrire
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
        )}

        {/* Onglet : Historique */}
        {tab === 'history' && (
          loadH ? <Loader/> : history.length === 0
            ? <EmptyState icon={<svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5"><path d="M3 3v5h5"/><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/></svg>}
                text={participant.is_visitor_church ? "Aucune présence enregistrée dans cette église." : "Aucune présence enregistrée."} />
            : <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {/* Bandeau visiteur inter-église */}
                {participant.is_visitor_church && (
                  <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: '10px 14px', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    <span style={{ fontSize: '0.78rem', color: '#b45309', fontWeight: 600 }}>
                      Historique de vos visites à <strong>{participant.visited_church_name || 'cette église'}</strong> en tant que visiteur externe.
                    </span>
                  </div>
                )}
                <div style={{ fontSize: '0.77rem', color: '#94a3b8', fontWeight: 700, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {history.length} présence{history.length > 1 ? 's' : ''} enregistrée{history.length > 1 ? 's' : ''}
                </div>
                {history.map((h, i) => {
                  const scanDate = h.recorded_at ? new Date(h.recorded_at) : null;
                  const dateLabel = scanDate ? scanDate.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : h.activity_date;
                  const timeLabel = scanDate ? scanDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : null;
                  const tc = h.activity_type_color || color;
                  return (
                    <div key={i} style={{ backgroundColor: 'white', borderRadius: '14px', padding: '13px 15px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: '13px' }}>
                      <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: `${tc}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={tc} strokeWidth="2">
                          <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                        </svg>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{h.activity_name}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '3px', flexWrap: 'wrap' }}>
                          {h.activity_type && <span style={{ fontSize: '0.66rem', fontWeight: 700, color: tc, backgroundColor: `${tc}15`, borderRadius: '999px', padding: '1px 7px' }}>{h.activity_type}</span>}
                          <span style={{ fontSize: '0.72rem', color: '#64748b' }}>{dateLabel}</span>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        {timeLabel && <div style={{ fontSize: '0.85rem', fontWeight: 800, color: tc, display: 'flex', alignItems: 'center', gap: '4px' }}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={tc} strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> {timeLabel}</div>}
                        {h.scanned_via_qr && (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', marginTop: '3px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '999px', padding: '2px 7px' }}>
                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/><path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/><rect x="7" y="7" width="3" height="3"/><rect x="14" y="14" width="3" height="3"/></svg>
                            <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#16a34a' }}>QR</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
        )}

        {/* Onglet : Galerie (photos, vidéos, diffusions en direct, affiches/publicités) */}
        {tab === 'gallery' && (
          <GalleryContent churchId={selectedChurch?.id} mobile accentColor={color} emptyText="Aucune photo, vidéo ou diffusion pour le moment." />
        )}
      </div>

      {/* ── BOTTOM NAVIGATION BAR ─────────────────────────────────────── */}
      <div style={{
        position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
        width: '100%', maxWidth: '520px',
        backgroundColor: 'white',
        borderTop: '1px solid #e2e8f0',
        display: 'flex',
        paddingBottom: 'env(safe-area-inset-bottom, 8px)',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.08)',
        zIndex: 100,
      }}>
        {tabs.map(t => {
          const active = tab === t.key;
          return (
            <button key={t.key}
              onClick={() => setTab(t.key)}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '3px', padding: '10px 4px 8px', background: 'none', border: 'none', cursor: 'pointer', position: 'relative' }}>
              {t.badge && <span style={{ position: 'absolute', top: '8px', right: '20%', minWidth: '17px', height: '17px', backgroundColor: '#ef4444', color: 'white', borderRadius: '999px', fontSize: '0.6rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>{t.badge}</span>}
              {t.icon(active)}
              <span style={{ fontSize: '0.62rem', fontWeight: active ? 800 : 500, color: active ? color : '#94a3b8', lineHeight: 1 }}>{t.label}</span>
              {active && <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '28px', height: '3px', backgroundColor: color, borderRadius: '0 0 3px 3px' }}/>}
            </button>
          );
        })}
      </div>

      {/* ── MODALS ─────────────────────────────────────────────────────── */}
      {showCard && <MemberCardModal qrCode={participant.qr_code} onClose={() => setShowCard(false)}/>}

      {showNotifPanel && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', zIndex: 900, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '70px 14px 14px' }}
          onClick={() => setShowNotifPanel(false)}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 440, maxHeight: '75vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontWeight: 800, fontSize: '0.92rem', color: '#1e293b' }}>Notifications</span>
              <button onClick={() => setShowNotifPanel(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, width: 28, height: 28, cursor: 'pointer', color: '#64748b', fontSize: 14 }}>✕</button>
            </div>
            {push.supported && push.permission !== 'denied' && !push.subscribed && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px', background: `${color}0d`, borderBottom: '1px solid #f1f5f9' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" style={{ flexShrink: 0 }}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                <div style={{ flex: 1, fontSize: '0.78rem', color: '#374151' }}>Recevez les annonces de l'église directement sur cet appareil.</div>
                <button onClick={() => push.subscribe()} disabled={push.loading}
                  style={{ background: color, color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: '0.78rem', fontWeight: 700, cursor: push.loading ? 'wait' : 'pointer', flexShrink: 0 }}>
                  {push.loading ? '...' : 'Activer'}
                </button>
              </div>
            )}
            {push.subscribed && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px', background: '#f0fdf4', borderBottom: '1px solid #f1f5f9' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" style={{ flexShrink: 0 }}><path d="M20 6L9 17l-5-5"/></svg>
                <div style={{ flex: 1, fontSize: '0.76rem', color: '#166534' }}>Notifications push activées</div>
                <button onClick={() => push.unsubscribe()} disabled={push.loading}
                  style={{ background: 'transparent', color: '#166534', border: '1px solid #bbf7d0', borderRadius: 8, padding: '5px 10px', fontSize: '0.72rem', fontWeight: 600, cursor: push.loading ? 'wait' : 'pointer', flexShrink: 0 }}>
                  Désactiver
                </button>
              </div>
            )}
            <div style={{ overflowY: 'auto' }}>
              {notifications.length === 0 ? (
                <div style={{ padding: '40px 20px', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>Aucune notification pour le moment.</div>
              ) : (
                notifications.map(n => (
                  <a key={n.id} href={n.url || undefined} target={n.url && n.url.startsWith('http') ? '_blank' : undefined} rel="noreferrer"
                    style={{ display: 'block', padding: '14px 18px', borderBottom: '1px solid #f8fafc', textDecoration: 'none', cursor: n.url ? 'pointer' : 'default' }}>
                    {n.image_url && <img src={n.image_url} alt="" style={{ width: '100%', borderRadius: 10, marginBottom: 8, maxHeight: 140, objectFit: 'cover' }} />}
                    <div style={{ fontWeight: 700, fontSize: '0.87rem', color: '#1e293b', marginBottom: 3 }}>{n.title}</div>
                    {n.body && <div style={{ fontSize: '0.8rem', color: '#64748b', lineHeight: 1.5, whiteSpace: 'pre-line' }}>{n.body}</div>}
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 6 }}>
                      {new Date(n.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </a>
                ))
              )}
            </div>
          </div>
        </div>
      )}
      {showScanner && (
        <QRScannerModal participant={participant}
          onClose={() => setShowScanner(false)}
          onSuccess={() => {
            refreshParticipant();
            setTab('history');
            setLoadH(true);
            api.get(`/participants/fidele/profile/${participant.qr_code}/`)
              .then(r => setHistory(r.data.history || []))
              .catch(() => {})
              .finally(() => setLoadH(false));
          }}
        />
      )}
      {showChangePwd && <ChangePasswordModal participant={participant} onClose={() => setShowChangePwd(false)}/>}
      {showMemberReqForm && (
        <MembershipRequestForm participant={participant}
          onClose={() => setShowMemberReqForm(false)}
          onSubmitted={() => { setShowMemberReqForm(false); fetchMemberReqStatus(); }}
        />
      )}
      {/* ── Modale détail activité ── */}
      {actDetail && (() => {
        const act = actDetail;
        const sc = ACT_STATUS[act.status] || ACT_STATUS.planned;
        const isReg = registeredIds.has(act.id);
        const acColor = act.activity_type_color || color;
        const fmt = d => { try { return new Date(d+'T00:00:00').toLocaleDateString('fr-FR',{weekday:'long',day:'numeric',month:'long',year:'numeric'}); } catch { return d; } };
        return (
          <div style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.65)', zIndex:200, display:'flex', alignItems:'flex-end', justifyContent:'center', backdropFilter:'blur(6px)' }}
            onClick={() => setActDetail(null)}>
            <div style={{ background:'white', borderRadius:'24px 24px 0 0', width:'100%', maxWidth:480, boxShadow:'0 -8px 40px rgba(0,0,0,0.2)', overflow:'hidden', maxHeight:'88vh', display:'flex', flexDirection:'column' }}
              onClick={e => e.stopPropagation()}>
              {/* Header coloré */}
              <div style={{ background:`linear-gradient(135deg,${acColor},${acColor}cc)`, padding:'20px 20px 16px', flexShrink:0, position:'relative' }}>
                <button onClick={() => setActDetail(null)} style={{ position:'absolute', top:12, right:12, background:'rgba(255,255,255,0.2)', border:'none', borderRadius:8, width:30, height:30, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'white' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
                <div style={{ display:'inline-flex', alignItems:'center', gap:5, background:'rgba(255,255,255,0.2)', borderRadius:999, padding:'3px 10px', marginBottom:8, fontSize:'0.7rem', fontWeight:700, color:'white' }}>
                  {sc.label}
                  {act.activity_type && <> · {act.activity_type}</>}
                </div>
                <div style={{ fontWeight:800, fontSize:'1.15rem', color:'white', lineHeight:1.25 }}>{act.name}</div>
              </div>
              {/* Corps scrollable */}
              <div style={{ overflowY:'auto', padding:'18px 20px', flex:1 }}>
                {/* Infos clés */}
                <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:16 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:32, height:32, borderRadius:9, background:`${acColor}15`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <CalendarIcon size={14} color={acColor}/>
                    </div>
                    <div>
                      <div style={{ fontSize:'0.68rem', color:'#94a3b8', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em' }}>Date</div>
                      <div style={{ fontWeight:700, fontSize:'0.9rem', color:'#1e293b' }}>{fmt(act.nextDate || act.date)}</div>
                    </div>
                  </div>
                  {act.start_time && (
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:32, height:32, borderRadius:9, background:`${acColor}15`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <ClockIcon size={14} color={acColor}/>
                      </div>
                      <div>
                        <div style={{ fontSize:'0.68rem', color:'#94a3b8', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em' }}>Horaire</div>
                        <div style={{ fontWeight:700, fontSize:'0.9rem', color:'#1e293b' }}>{act.start_time.slice(0,5)}{act.end_time ? ` — ${act.end_time.slice(0,5)}` : ''}</div>
                      </div>
                    </div>
                  )}
                  {act.location && (
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:32, height:32, borderRadius:9, background:`${acColor}15`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <MapPinIcon size={14} color={acColor}/>
                      </div>
                      <div>
                        <div style={{ fontSize:'0.68rem', color:'#94a3b8', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em' }}>Lieu</div>
                        <div style={{ fontWeight:700, fontSize:'0.9rem', color:'#1e293b' }}>{act.location}</div>
                      </div>
                    </div>
                  )}
                </div>
                {/* Description */}
                {act.description && (
                  <div style={{ background:'#f8fafc', borderRadius:12, padding:'12px 14px', marginBottom:16 }}>
                    <div style={{ fontSize:'0.68rem', color:'#94a3b8', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:5 }}>Description</div>
                    <p style={{ fontSize:'0.88rem', color:'#475569', lineHeight:1.7, margin:0 }}>{act.description}</p>
                  </div>
                )}
                {/* Actions */}
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {isReg ? (
                    <>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'13px', borderRadius:12, background:'#f0fdf4', border:'1px solid #bbf7d0', fontWeight:700, fontSize:'0.92rem', color:'#16a34a' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                        Vous êtes inscrit(e) à cette activité
                      </div>
                      <button style={{ padding:'11px', borderRadius:12, border:'1px solid #fecaca', background:'#fef2f2', color:'#b91c1c', fontSize:'0.88rem', fontWeight:700, cursor:'pointer' }}
                        onClick={() => { setUnregTarget(myRegs.find(r => r.activity_id === act.id)); setActDetail(null); }}>
                        Annuler mon inscription
                      </button>
                    </>
                  ) : (
                    <button style={{ width:'100%', padding:'14px', background:`linear-gradient(135deg,${acColor},${acColor}cc)`, color:'white', border:'none', borderRadius:12, fontWeight:800, fontSize:'0.95rem', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}
                      onClick={() => { setSelected(act); setActDetail(null); }}>
                      M'inscrire à cette activité
                      <ArrowRightIcon size={15} color="white"/>
                    </button>
                  )}
                  <button onClick={() => setActDetail(null)} style={{ padding:'11px', borderRadius:12, border:'1px solid #e2e8f0', background:'#f8fafc', color:'#64748b', fontWeight:600, fontSize:'0.88rem', cursor:'pointer' }}>
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {selected && (
        <RegisterModal activity={selected} participant={participant}
          onClose={() => setSelected(null)}
          onSuccess={msg => { setOk(msg); setSelected(null); fetchMyRegs(); fetchActivities(); }}
        />
      )}

      {/* Modal édition profil (bottom sheet) */}
      {showEditProfile && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.58)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowEditProfile(false)}>
          <div style={{ backgroundColor: 'white', borderRadius: '20px 20px 0 0', padding: '22px 20px 32px', width: '100%', maxWidth: '520px', maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            {/* Handle */}
            <div style={{ width: '36px', height: '4px', backgroundColor: '#d1d5db', borderRadius: '99px', margin: '0 auto 16px' }}/>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ fontWeight: 800, fontSize: '1.05rem', color: '#1e293b' }}>Modifier mes informations</div>
              <button style={s.btnClose} onClick={() => setShowEditProfile(false)}><XIcon size={17}/></button>
            </div>
            <form onSubmit={handleSaveProfile}>
              {editError && <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '9px 13px', color: '#b91c1c', fontSize: '0.84rem', marginBottom: '14px' }}>{editError}</div>}
              <p style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '14px', marginTop: '-6px' }}>Laissez vide pour garder la valeur actuelle.</p>
              {profileIncomplete && (
                <div style={{ marginBottom: '11px' }}>
                  <label style={s.label}>Mon église / Centre</label>
                  <select style={s.input} value={editForm.church_id} onChange={e => setEditForm(f => ({ ...f, church_id: e.target.value }))}>
                    <option value="">Choisir mon église…</option>
                    {editChurches.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
                  </select>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '11px', marginBottom: '11px' }}>
                <div><label style={s.label}>Prénom</label><input style={s.input} value={editForm.first_name} onChange={e => setEditForm(f => ({ ...f, first_name: e.target.value }))} placeholder={participant.first_name}/></div>
                <div><label style={s.label}>Nom</label><input style={s.input} value={editForm.last_name} onChange={e => setEditForm(f => ({ ...f, last_name: e.target.value }))} placeholder={participant.last_name}/></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '11px', marginBottom: '11px' }}>
                <div><label style={s.label}>Téléphone</label><input style={s.input} value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} placeholder={participant.phone || '6XX XXX XXX'}/></div>
                <div><label style={s.label}>WhatsApp</label><input style={s.input} value={editForm.whatsapp} onChange={e => setEditForm(f => ({ ...f, whatsapp: e.target.value }))} placeholder={participant.whatsapp || '6XX XXX XXX'}/></div>
              </div>
              <div style={{ marginBottom: '11px' }}><label style={s.label}>Email</label><input type="email" style={s.input} value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} placeholder={participant.email || 'votre@email.com'}/></div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '11px', marginBottom: '11px' }}>
                <div><label style={s.label}>Ville</label><input style={s.input} value={editForm.city} onChange={e => setEditForm(f => ({ ...f, city: e.target.value }))} placeholder={participant.city || 'Ville'}/></div>
                <div><label style={s.label}>Profession</label><input style={s.input} value={editForm.profession} onChange={e => setEditForm(f => ({ ...f, profession: e.target.value }))} placeholder={participant.profession || 'Profession'}/></div>
              </div>
              <div style={{ marginBottom: '18px' }}><label style={s.label}>Adresse</label><input style={s.input} value={editForm.address} onChange={e => setEditForm(f => ({ ...f, address: e.target.value }))} placeholder={participant.address || 'Adresse'}/></div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" style={{ ...s.btnSecondary, flex: 1 }} onClick={() => setShowEditProfile(false)}>Annuler</button>
                <button type="submit" style={{ ...s.btnPrimary, flex: 2 }} disabled={editSaving}>{editSaving ? 'Sauvegarde...' : 'Enregistrer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal désinscription */}
      {unregTarget && (
        <div style={s.overlay} onClick={() => setUnregTarget(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b', marginBottom: '10px' }}>Se désinscrire</div>
            <p style={{ fontSize: '0.88rem', color: '#64748b', marginBottom: '16px' }}>Annuler l'inscription à <strong>« {unregTarget.activity_name} »</strong> ?</p>
            <div style={{ display: 'flex', gap: '9px' }}>
              <button style={{ ...s.btnSecondary, flex: 1 }} onClick={() => setUnregTarget(null)}>Annuler</button>
              <button style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', backgroundColor: '#ef4444', color: 'white', fontWeight: 700, cursor: 'pointer' }}
                onClick={() => handleUnregister(unregTarget)}>Confirmer</button>
            </div>
          </div>
        </div>
      )}
      </div>{/* end centering wrapper */}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SÉLECTION D'ÉGLISE — premier écran public
═══════════════════════════════════════════════════════════════════════════ */
function ChurchSelectScreen({ onSelect, onBack }) {
  const [churches, setChurches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/churches/public/`)
      .then(r => setChurches(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={s.wrap}>
      <div style={{ ...s.welcomeCard, maxWidth: '480px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        {onBack && <BtnBack onClick={onBack} />}
        <div style={s.logoBlock}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#1e40af" strokeWidth="1.4">
            <circle cx="12" cy="12" r="10"/>
            <line x1="2" y1="12" x2="22" y2="12"/>
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
          </svg>
          <h1 style={s.bigTitle}>Choisissez votre église</h1>
          <p style={s.sub}>Sélectionnez l'église à laquelle vous appartenez</p>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>Chargement...</div>
        ) : churches.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>Aucune église disponible.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', overflowY: 'auto', paddingRight: '4px' }}>
            {churches.map(ch => (
              <button
                key={ch.id}
                onClick={() => onSelect(ch)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '14px',
                  padding: '14px 16px', borderRadius: '12px', border: '1.5px solid #e2e8f0',
                  background: 'white', cursor: 'pointer', textAlign: 'left',
                  transition: 'all 0.15s', width: '100%',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = ch.primary_color || '#3b82f6'; e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = 'white'; }}
              >
                {ch.logo_url
                  ? <img src={ch.logo_url} alt="" style={{ width: '44px', height: '44px', borderRadius: '10px', objectFit: 'cover', flexShrink: 0 }} />
                  : <div style={{ width: '44px', height: '44px', borderRadius: '10px', flexShrink: 0, background: ch.primary_color || '#1e40af', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: '1.2rem' }}>
                      {ch.name[0]}
                    </div>
                }
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>{ch.name}</div>
                  {ch.pastor && <div style={{ fontSize: '0.78rem', color: ch.primary_color || '#1e40af', fontWeight: 600, marginTop: '1px' }}>✝ Pasteur {ch.pastor}</div>}
                  {ch.city && <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '1px' }}>📍 {ch.city}{ch.country ? `, ${ch.country}` : ''}</div>}
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   COMPOSANT RACINE
═══════════════════════════════════════════════════════════════════════════ */
const SESSION_KEY = 'fidele_participant';
const CHURCH_KEY  = 'fidele_church';

function HomeButton() {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate('/')}
      title="Retour à l'accueil"
      style={{
        position: 'fixed', top: '14px', left: '14px', zIndex: 9999,
        display: 'flex', alignItems: 'center', gap: '6px',
        background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)',
        border: '1.5px solid rgba(255,255,255,0.35)', borderRadius: '10px',
        padding: '7px 14px', color: 'white', fontWeight: 700, fontSize: '0.82rem',
        cursor: 'pointer', transition: 'background 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.30)'}
      onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
      Accueil
    </button>
  );
}

// Session fidèle permanente (localStorage) : le fidèle reste connecté après
// fermeture de l'onglet ou retour arrière, jusqu'à « Déco. ». Les sessions
// ouvertes avant ce changement (sessionStorage) sont reprises automatiquement.
function readSession(key) {
  try {
    const saved = localStorage.getItem(key) ?? sessionStorage.getItem(key);
    return saved ? JSON.parse(saved) : null;
  } catch { return null; }
}

function writeSession(key, value) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
    sessionStorage.removeItem(key);
  } catch { /* stockage indisponible (navigation privée) : session limitée à la page */ }
}

export default function FidelePortal() {
  const [selectedChurch, setSelectedChurch] = useState(() => readSession(CHURCH_KEY));
  const [participant, setParticipant] = useState(() => readSession(SESSION_KEY));

  // Lire l'action depuis l'URL (?action=login ou ?action=create)
  const urlAction = new URLSearchParams(window.location.search).get('action');
  const [screen, setScreen] = useState(() => {
    if (urlAction === 'login') return 'login';
    if (urlAction === 'create') return 'nouveau-fidele';
    return 'welcome';
  });

  const handleLogin = (p) => {
    if (p.church) {
      writeSession(CHURCH_KEY, p.church);
      setSelectedChurch(p.church);
    }
    writeSession(SESSION_KEY, p);
    setParticipant(p);
  };

  const handleLogout = () => {
    writeSession(SESSION_KEY, null);
    writeSession(CHURCH_KEY, null);
    setParticipant(null);
    setSelectedChurch(null);
    setScreen('welcome');
  };

  if (participant) {
    // Pas de HomeButton ici : le Dashboard a déjà sa propre barre de navigation (logo, "Ma carte", "Déco.")
    return <Dashboard participant={participant} onLogout={handleLogout} onParticipantUpdate={handleLogin} selectedChurch={selectedChurch}/>;
  }
  if (screen === 'login')          return <><HomeButton /><LoginScreen onLogin={handleLogin} onBack={() => setScreen('welcome')}/></>;
  if (screen === 'nouveau-fidele') return <><HomeButton /><NouveauFideleForm onCreated={handleLogin} onBack={() => setScreen('welcome')} onLogin={() => setScreen('login')}/></>;
  if (screen === 'visiteur')       return <><HomeButton /><VisiteurForm onBack={() => setScreen('welcome')} onCreated={handleLogin} onLogin={() => setScreen('login')}/></>;
  return (
    <>
      <HomeButton />
      <WelcomeScreen
        onMembre={() => setScreen('login')}
        onNouveauFidele={() => setScreen('nouveau-fidele')}
        onVisiteur={() => setScreen('visiteur')}
      />
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MICRO-COMPOSANTS
═══════════════════════════════════════════════════════════════════════════ */
function BtnBack({ onClick, label = 'Retour' }) {
  return (
    <button style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '0.84rem', fontWeight: 600, marginBottom: '14px', padding: 0 }} onClick={onClick}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
      {label}
    </button>
  );
}
// Numéro déjà utilisé à la création de compte : on oriente vers la connexion
// (le code membre existant n'est jamais affiché, il sert d'identifiant)
function AccountExists({ msg, onLogin }) {
  return (
    <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <span style={{ color: '#b91c1c', fontSize: '0.86rem', fontWeight: 600 }}>{msg}</span>
      {onLogin && (
        <button type="button" onClick={onLogin}
          style={{ alignSelf: 'flex-start', padding: '8px 16px', borderRadius: '8px', border: 'none', background: '#1e40af', color: 'white', fontWeight: 700, fontSize: '0.84rem', cursor: 'pointer' }}>
          Me connecter
        </button>
      )}
    </div>
  );
}

function Field({ label, error, children }) {
  // « Libellé * » = champ obligatoire : l'astérisque est affiché en rouge
  const required = typeof label === 'string' && label.endsWith(' *');
  return (
    <div>
      <label style={s.label}>{required ? label.slice(0, -2) : label}{required && <Req />}</label>
      {children}
      {error && <span role="alert" style={{ fontSize: '0.76rem', color: ERROR_COLOR, marginTop: '3px', display: 'block' }}>{Array.isArray(error) ? error[0] : error}</span>}
    </div>
  );
}
function Err({ msg }) {
  return <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '7px', padding: '9px 13px', color: '#b91c1c', fontSize: '0.86rem' }}>{msg}</div>;
}
function MetaRow({ icon, children }) {
  return <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: '#64748b' }}>{icon}{children}</span>;
}
function Loader() {
  return <div style={{ textAlign: 'center', padding: '36px', color: '#64748b', fontSize: '0.9rem' }}>Chargement...</div>;
}
function EmptyState({ icon, text }) {
  return <div style={{ textAlign: 'center', padding: '44px 16px', color: '#94a3b8', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>{icon}<p style={{ margin: 0 }}>{text}</p></div>;
}

// États d'affichage de la liste d'activités vide (RCC-COR-001, cas B / C / D)
function ActivitiesEmpty({ error, incomplete, filtered, search, color, onRetry, onComplete }) {
  const icon = <CalendarIcon size={38} color="#cbd5e1"/>;
  const btn = { marginTop: '4px', padding: '9px 18px', borderRadius: '10px', border: 'none', background: color, color: 'white', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' };
  if (error) return (
    <div style={{ textAlign: 'center', padding: '44px 16px', color: '#94a3b8', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
      {icon}
      <p style={{ margin: 0 }}>Les activités ne peuvent pas être chargées pour le moment. Veuillez réessayer ou contacter le support RCC Connect.</p>
      <button style={btn} onClick={onRetry}>Réessayer</button>
    </div>
  );
  if (incomplete) return (
    <div style={{ textAlign: 'center', padding: '44px 16px', color: '#94a3b8', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
      {icon}
      <p style={{ margin: 0 }}>Votre profil doit être complété pour afficher les activités correspondant à votre Centre.</p>
      <button style={btn} onClick={onComplete}>Compléter mon profil</button>
    </div>
  );
  if (filtered) return <EmptyState icon={icon} text={search ? `Aucun résultat pour "${search}"` : 'Aucune activité ne correspond à ces filtres.'} />;
  return <EmptyState icon={icon} text="Aucune activité n'est actuellement disponible pour votre profil." />;
}

/* ═══════════════════════════════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════════════════════════════ */
const s = {
  wrap:       { minHeight: '100vh', background: 'linear-gradient(135deg,#1e3a8a 0%,#1e40af 55%,#2563eb 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  welcomeCard:{ backgroundColor: 'white', borderRadius: '18px', padding: 'clamp(20px, 6vw, 38px) clamp(16px, 5vw, 32px)', width: '100%', maxWidth: '460px', boxShadow: '0 24px 64px rgba(0,0,0,0.28)' },
  card:       { backgroundColor: 'white', borderRadius: '16px', padding: 'clamp(20px, 6vw, 34px)', width: '100%', maxWidth: '440px', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' },
  logoBlock:  { textAlign: 'center', marginBottom: '26px' },
  bigTitle:   { fontSize: '1.55rem', fontWeight: 800, color: '#1e40af', margin: '0 0 4px' },
  sub:        { fontSize: '0.88rem', color: '#64748b', margin: 0 },
  choiceBtn:  { display: 'flex', alignItems: 'center', gap: '13px', padding: '14px 16px', backgroundColor: 'white', border: '1.5px solid #e2e8f0', borderRadius: '12px', cursor: 'pointer', width: '100%' },
  choiceIcon: { width: '50px', height: '50px', borderRadius: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  label:      { display: 'block', fontSize: '0.83rem', fontWeight: 600, color: '#374151', marginBottom: '5px' },
  input:      { width: '100%', padding: '9px 13px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.93rem', color: '#1e293b', boxSizing: 'border-box' },
  btnPrimary: { backgroundColor: '#1e40af', color: 'white', border: 'none', borderRadius: '8px', padding: '11px 18px', cursor: 'pointer', fontSize: '0.93rem', fontWeight: 700 },
  btnSecondary:{ backgroundColor: '#f1f5f9', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', padding: '10px 16px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 },
  btnWhite:   { display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: 'white', color: '#1e293b', border: '1px solid #d1d5db', borderRadius: '8px', padding: '7px 13px', cursor: 'pointer', fontSize: '0.83rem', fontWeight: 600 },
  btnClose:   { background: '#f1f5f9', border: 'none', borderRadius: '6px', cursor: 'pointer', padding: '5px', display: 'flex', alignItems: 'center', color: '#64748b' },
  cardBtn:    { display: 'flex', alignItems: 'center', gap: '5px', backgroundColor: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '7px', padding: '6px 11px', cursor: 'pointer', fontSize: '0.81rem', fontWeight: 600 },
  logoutBtn:  { backgroundColor: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '7px', padding: '6px 11px', cursor: 'pointer', fontSize: '0.81rem' },
  overlay:    { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.58)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '18px' },
  modal:      { backgroundColor: 'white', borderRadius: '13px', padding: '22px', width: '100%', maxWidth: '450px' },
  badge:      { fontSize: '0.73rem', padding: '2px 9px', borderRadius: '999px', fontWeight: 600 },
  successBox: { backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '9px 13px', color: '#166534', fontSize: '0.86rem', display: 'flex', alignItems: 'center', gap: '7px' },
};
